import React, { useEffect, useState } from 'react';
import { Bot, Loader2, RefreshCw, CheckCircle, XCircle, AlertTriangle, TrendingUp, ShieldAlert, BarChart3, Sparkles, Search, Microscope, Droplet, Eye, ShieldCheck } from 'lucide-react';
import { apiGet, apiPost } from '../data/api';

const VERDICT_COLORS = {
  TAKE: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
  CAUTION: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
  SKIP: 'text-red-400 bg-red-500/10 border-red-500/30',
};
const VERDICT_ICONS = { TAKE: CheckCircle, CAUTION: AlertTriangle, SKIP: XCircle };
const GRADE_COLORS = { A: 'text-emerald-400', B: 'text-green-400', C: 'text-amber-400', D: 'text-orange-400', F: 'text-red-400' };

export default function AIAdvisorHistory() {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [reviewLoading, setReviewLoading] = useState(false);
  const [systemReview, setSystemReview] = useState(null);
  const [tab, setTab] = useState('health');
  const [refreshingResults, setRefreshingResults] = useState(false);
  const [refreshMsg, setRefreshMsg] = useState(null);
  const [rescuing, setRescuing] = useState(false);
  const [duStats, setDuStats] = useState(null);
  const [autopsy, setAutopsy] = useState(null);
  const [autopsyLoading, setAutopsyLoading] = useState(false);
  const [autopsySource, setAutopsySource] = useState('all');

  const fetchHistory = () => {
    setLoading(true);
    apiGet(`/tt/admin/ai-advisor-history?days=${days}`)
      .then(d => setHistory(d))
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchHistory(); }, [days]);
  useEffect(() => {
    apiGet('/tt/admin/deep-underdog-stats?days=14')
      .then(setDuStats)
      .catch(() => {});
  }, [history]);

  const refreshResults = async () => {
    setRefreshingResults(true);
    setRefreshMsg(null);
    try {
      const r = await apiPost('/tt/admin/ai-advisor-refresh-results', {});
      const s = r?.settle || {};
      setRefreshMsg(`Settled ${s.settled ?? 0} • Results sent ${s.results_sent ?? 0}`);
      fetchHistory();
    } catch (e) {
      setRefreshMsg('Errore aggiornamento');
    }
    setRefreshingResults(false);
    setTimeout(() => setRefreshMsg(null), 6000);
  };

  const runAutopsy = async (src = autopsySource) => {
    setAutopsyLoading(true);
    try {
      // days=0 "Oggi" = da mezzanotte ora italiana (gestito dal backend).
      // Non forziamo a days=1 qui, altrimenti il report mostra "ultime 24h"
      // invece del vero "oggi" e l'utente vede alert di ieri.
      const r = await apiGet(`/tt/admin/loss-autopsy?days=${days}&source=${src}&min_sample=10`);
      setAutopsy(r);
    } catch (e) {
      setAutopsy({ error: e.message || 'failed' });
    } finally {
      setAutopsyLoading(false);
    }
  };

  const rescueTargeted = async () => {
    setRescuing(true);
    setRefreshMsg(null);
    try {
      const r = await apiPost('/tt/admin/rescue-targeted?max_matches=100', {});
      if (r?.error) {
        setRefreshMsg(`Errore targeted: ${r.error}`);
      } else {
        setRefreshMsg(`Targeted: ${r.rescued || 0}/${r.attempted || 0} match recuperati (target ${r.targets_total || 0})`);
        fetchHistory();
      }
    } catch (e) {
      setRefreshMsg(`Errore targeted: ${e.message || 'failed'}`);
    }
    setRescuing(false);
    setTimeout(() => setRefreshMsg(null), 8000);
  };

  const diagnosePending = async () => {
    setRescuing(true);
    setRefreshMsg(null);
    try {
      const r = await apiGet('/tt/admin/diagnose-pending-takes?days=30');
      const b = r?.breakdown || {};
      const unrec = b.pending_no_source_id + b.pending_too_old + b.not_in_db;
      const rec = b.pending_rescuable;
      const live = b.pending_live + b.pending_scheduled_future;
      setRefreshMsg(`📊 ${r.total_takes} TAKE — ✅ ${b.finished_ok} settled | 🔄 ${rec} rescuable | ⚠️ ${unrec} NON rescuable | ⏳ ${live} live/futuri`);
    } catch (e) {
      setRefreshMsg(`Errore diagnose: ${e.message || 'failed'}`);
    }
    setRescuing(false);
    setTimeout(() => setRefreshMsg(null), 20000);
  };

  const forceVoidStale = async () => {
    if (!window.confirm('Marcare come VOID i TAKE su match expired >3 giorni? Azione irreversibile sulle statistiche.')) return;
    setRescuing(true);
    setRefreshMsg(null);
    try {
      const r = await apiPost('/tt/admin/force-void-stale-takes?min_age_days=3', {});
      if (r?.reason === 'no_candidates') {
        setRefreshMsg('Nessun TAKE da marcare come void.');
      } else {
        setRefreshMsg(`Voided ${r.voided || 0} TAKE stale. Ricarica dati...`);
        fetchHistory();
      }
    } catch (e) {
      setRefreshMsg(`Errore void: ${e.message || 'failed'}`);
    }
    setRescuing(false);
    setTimeout(() => setRefreshMsg(null), 10000);
  };

  const rescueBetsapi = async () => {
    setRescuing(true);
    setRefreshMsg(null);
    try {
      const r = await apiPost('/tt/admin/rescue-stale-matches?max_matches=50', {});
      if (r?.error) {
        setRefreshMsg(`Errore rescue: ${r.error}`);
      } else {
        const recovered = (r?.rescued_already_finished || 0) + (r?.rescued || 0);
        setRefreshMsg(`Rescue: ${recovered} match recuperati da BetsAPI`);
        fetchHistory();
      }
    } catch (e) {
      setRefreshMsg(`Errore rescue: ${e.message || 'failed'}`);
    }
    setRescuing(false);
    setTimeout(() => setRefreshMsg(null), 8000);
  };

  const triggerReview = async () => {
    setReviewLoading(true);
    setTab('review');
    try {
      await apiPost('/tt/admin/ai-system-review');
      // Poll for result — backend hard timeout is 180s. Poll slightly longer
      // so we surface the real backend result instead of a client-side timeout
      // that leaves the user confused (2026-04-22 prod regression).
      const poll = async () => {
        for (let i = 0; i < 48; i++) {      // 48 × 5s = 240s > backend 180s
          await new Promise(r => setTimeout(r, 5000));
          try {
            const status = await apiGet('/tt/admin/ai-system-review');
            if (!status.running && status.last_result) {
              setSystemReview(status.last_result);
              setReviewLoading(false);
              return;
            }
          } catch { /* continue polling */ }
        }
        setSystemReview({ error: 'Timeout — riprova tra poco' });
        setReviewLoading(false);
      };
      poll();
    } catch (e) {
      setSystemReview({ error: e.message });
      setReviewLoading(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-zinc-500" size={24} /></div>;

  const picks = history?.picks || [];
  const reviews = history?.reviews || [];
  const verdicts = history?.verdicts || {};
  const takePicks = picks.filter(p => p.verdict === 'TAKE');
  const settledTakes = takePicks.filter(p => p.match_result === 'finished');

  return (
    <div data-testid="ai-advisor-history" className="space-y-5 max-w-[1200px]">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Sparkles size={24} className="text-violet-400" /> AI Advisor
          </h1>
          <p className="text-zinc-500 text-sm mt-1">Storico analisi e review sistema — Claude Sonnet</p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-300"
            data-testid="ai-advisor-days-select"
          >
            <option value={0}>Oggi</option>
            <option value={7}>7 giorni</option>
            <option value={14}>14 giorni</option>
            <option value={30}>30 giorni</option>
            <option value={90}>90 giorni</option>
          </select>
          <button
            data-testid="refresh-ai-results-btn"
            onClick={refreshResults}
            disabled={refreshingResults}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm font-medium transition disabled:opacity-50 border border-zinc-700"
            title="Forza settlement AI TAKE + ricarica WIN/LOSS"
          >
            {refreshingResults ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Aggiorna risultati
          </button>
          <button
            data-testid="rescue-targeted-btn"
            onClick={rescueTargeted}
            disabled={rescuing}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600/90 hover:bg-emerald-500 text-white text-sm font-medium transition disabled:opacity-50"
            title="Rescue solo dei match linked a alert/TAKE pending (veloce, prioritario)"
          >
            {rescuing ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            Rescue Targeted
          </button>
          <button
            data-testid="diagnose-pending-btn"
            onClick={diagnosePending}
            disabled={rescuing}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600/90 hover:bg-sky-500 text-white text-sm font-medium transition disabled:opacity-50"
            title="Diagnosi: quanti TAKE sono rescuable vs non-rescuable"
          >
            {rescuing ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            Diagnose
          </button>
          <button
            data-testid="void-stale-btn"
            onClick={forceVoidStale}
            disabled={rescuing}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600/80 hover:bg-red-500 text-white text-sm font-medium transition disabled:opacity-50"
            title="Marca come VOID i TAKE su match expired >3gg (pulisce stats)"
          >
            Void Stale
          </button>
          <button
            data-testid="rescue-betsapi-btn"
            onClick={rescueBetsapi}
            disabled={rescuing}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-amber-600/90 hover:bg-amber-500 text-white text-sm font-medium transition disabled:opacity-50"
            title="Recupera risultati mancanti direttamente da BetsAPI (per pick unresolved)"
          >
            {rescuing ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
            Rescue BetsAPI
          </button>
          {refreshMsg && <span className="text-xs text-emerald-400 whitespace-nowrap">{refreshMsg}</span>}
          <button
            onClick={triggerReview}
            disabled={reviewLoading}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium transition disabled:opacity-50"
          >
            {reviewLoading ? <Loader2 size={14} className="animate-spin" /> : <Bot size={14} />}
            System Review
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatCard label="Analizzati" value={history?.total_analyzed || 0} icon={BarChart3} />
        <StatCard label="TAKE" value={verdicts.TAKE || 0} color="text-emerald-400" icon={CheckCircle} />
        <StatCard label="SKIP" value={verdicts.SKIP || 0} color="text-red-400" icon={XCircle} />
        <StatCard label="CAUTION" value={verdicts.CAUTION || 0} color="text-amber-400" icon={AlertTriangle} />
        <StatCard label="Settled TAKE" value={history?.settled_takes || 0} icon={TrendingUp} />
      </div>

      {/* TAKE Performance */}
      {history?.settled_takes > 0 && history?.take_results && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <StatCard label="Win" value={history.take_results.wins} color="text-emerald-400" icon={CheckCircle} />
          <StatCard label="Loss" value={history.take_results.losses} color="text-red-400" icon={XCircle} />
          <StatCard label="Void" value={history.take_results.voids} color="text-zinc-400" icon={AlertTriangle} />
          <StatCard
            label="P&L"
            value={(history.take_results.total_profit >= 0 ? '+' : '') + history.take_results.total_profit}
            color={history.take_results.total_profit >= 0 ? 'text-emerald-400' : 'text-red-400'}
            icon={BarChart3}
          />
          <StatCard
            label="ROI"
            value={(history.take_results.roi_pct >= 0 ? '+' : '') + history.take_results.roi_pct + '%'}
            color={history.take_results.roi_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}
            icon={TrendingUp}
          />
        </div>
      )}

      {/* Market Breakdown: Win/Loss per MW/OU/HC */}
      {history?.market_breakdown && Object.keys(history.market_breakdown).length > 0 && (
        <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4">
          <div className="flex items-center gap-2 mb-3 text-zinc-400 text-xs uppercase tracking-wider font-semibold">
            <BarChart3 size={14} />
            Breakdown per Mercato
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {['match_winner', 'over_under', 'handicap', 'other']
              .filter(k => history.market_breakdown[k])
              .map(mk => {
                const b = history.market_breakdown[mk];
                const cfgMap = {
                  match_winner: { title: 'Match Winner', short: 'MW', border: 'border-blue-500/20', bg: 'bg-blue-500/5', badgeBg: 'bg-blue-500/15', badgeText: 'text-blue-400', badgeBorder: 'border-blue-500/30' },
                  over_under:   { title: 'Over / Under', short: 'U/O', border: 'border-purple-500/20', bg: 'bg-purple-500/5', badgeBg: 'bg-purple-500/15', badgeText: 'text-purple-400', badgeBorder: 'border-purple-500/30' },
                  handicap:     { title: 'Handicap', short: 'HC', border: 'border-amber-500/20', bg: 'bg-amber-500/5', badgeBg: 'bg-amber-500/15', badgeText: 'text-amber-400', badgeBorder: 'border-amber-500/30' },
                  other:        { title: 'Altri', short: '?', border: 'border-zinc-700', bg: 'bg-zinc-800/20', badgeBg: 'bg-zinc-700', badgeText: 'text-zinc-400', badgeBorder: 'border-zinc-600' },
                };
                const cfg = cfgMap[mk];
                const resolved = b.wins + b.losses;
                return (
                  <div
                    key={mk}
                    data-testid={`market-breakdown-${mk}`}
                    className={`rounded-lg border ${cfg.border} ${cfg.bg} p-3`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 text-[10px] font-bold rounded ${cfg.badgeBg} ${cfg.badgeText} border ${cfg.badgeBorder}`}>
                          {cfg.short}
                        </span>
                        <span className="text-sm font-medium text-zinc-200">{cfg.title}</span>
                      </div>
                      <span className="text-[10px] uppercase text-zinc-500">
                        {resolved} pick
                      </span>
                    </div>
                    <div className="grid grid-cols-3 gap-1 mb-2 text-center">
                      <div>
                        <div className="text-lg font-bold text-emerald-400 tabular-nums">{b.wins}</div>
                        <div className="text-[10px] uppercase text-zinc-500">Win</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-red-400 tabular-nums">{b.losses}</div>
                        <div className="text-[10px] uppercase text-zinc-500">Loss</div>
                      </div>
                      <div>
                        <div className="text-lg font-bold text-zinc-500 tabular-nums">{b.voids}</div>
                        <div className="text-[10px] uppercase text-zinc-500">Void</div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-2 border-t border-zinc-800 text-xs">
                      <span className="text-zinc-400">
                        WR <span className="font-mono text-zinc-200">{b.win_rate_pct}%</span>
                      </span>
                      <span className={b.total_profit >= 0 ? 'text-emerald-400 font-medium' : 'text-red-400 font-medium'}>
                        {b.total_profit >= 0 ? '+' : ''}{b.total_profit}
                      </span>
                      <span className={b.roi_pct >= 0 ? 'text-emerald-400 font-mono' : 'text-red-400 font-mono'}>
                        ROI {b.roi_pct >= 0 ? '+' : ''}{b.roi_pct}%
                      </span>
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Loss Autopsy — on-demand drill-down of losing buckets */}
      <LossAutopsyCard
        autopsy={autopsy}
        loading={autopsyLoading}
        source={autopsySource}
        setSource={(s) => { setAutopsySource(s); if (autopsy) runAutopsy(s); }}
        onRun={() => runAutopsy()}
        days={days}
      />

      {/* Deep Underdog telemetry card */}
      {duStats && duStats.total_picks > 0 && (
        <div data-testid="deep-underdog-card" className="rounded-xl border border-rose-500/20 bg-rose-500/5 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="px-2 py-0.5 text-[10px] font-bold rounded bg-rose-500/15 text-rose-400 border border-rose-500/30">
                DEEP UD
              </span>
              <span className="text-sm font-medium text-zinc-200">Deep Underdog Tier</span>
              <span className="text-xs text-zinc-500">({duStats.window_days}gg)</span>
            </div>
            <span className="text-[10px] uppercase text-zinc-500">{duStats.total_picks} pick</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center">
            <div><div className="text-lg font-bold text-emerald-400 tabular-nums">{duStats.wins}</div><div className="text-[10px] uppercase text-zinc-500">Win</div></div>
            <div><div className="text-lg font-bold text-red-400 tabular-nums">{duStats.losses}</div><div className="text-[10px] uppercase text-zinc-500">Loss</div></div>
            <div><div className="text-lg font-bold text-zinc-500 tabular-nums">{duStats.voids}</div><div className="text-[10px] uppercase text-zinc-500">Void</div></div>
            <div><div className="text-lg font-bold text-zinc-300 tabular-nums">{duStats.win_rate_pct}%</div><div className="text-[10px] uppercase text-zinc-500">WR</div></div>
            <div><div className={`text-lg font-bold tabular-nums ${duStats.roi_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>{duStats.roi_pct >= 0 ? '+' : ''}{duStats.roi_pct}%</div><div className="text-[10px] uppercase text-zinc-500">ROI</div></div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-800 overflow-x-auto">
        {[['health', 'Health'], ['picks', 'Pick AI'], ['review', 'System Review'], ['patterns', 'Pattern Detective'], ['patterngate', 'Pattern Gate'], ['filterdiff', 'Filter Diff'], ['calibrator', 'Model Calibrator'], ['edges', 'Edge Finder'], ['anomalies', 'Anomaly Monitor'], ['calibration', 'Calibration'], ['drift', 'Drift']].map(([key, label]) => (
          <button
            key={key}
            data-testid={`tab-${key}-btn`}
            onClick={() => setTab(key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition ${tab === key ? 'border-violet-500 text-violet-400' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {tab === 'health' && <HealthTab />}
      {tab === 'picks' && <PicksTab picks={picks} />}
      {tab === 'review' && <ReviewTab reviews={reviews} systemReview={systemReview} />}
      {tab === 'patterns' && <PatternTab />}
      {tab === 'patterngate' && <PatternGateTab />}
      {tab === 'filterdiff' && <FilterDiffTab />}
      {tab === 'calibrator' && <CalibratorTab />}
      {tab === 'edges' && <EdgeFinderTab />}
      {tab === 'anomalies' && <AnomalyTab />}
      {tab === 'calibration' && <CalibrationTab />}
      {tab === 'drift' && <DriftTab />}
    </div>
  );
}

function StatCard({ label, value, color = 'text-white', icon: Icon }) {
  return (
    <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 p-4">
      <div className="flex items-center gap-2 text-zinc-500 text-xs mb-1">
        <Icon size={13} /> {label}
      </div>
      <div className={`text-2xl font-bold ${color}`}>{value}</div>
    </div>
  );
}

function LossAutopsyCard({ autopsy, loading, source, setSource, onRun, days }) {
  const hasData = autopsy && !autopsy.error && typeof autopsy.total_settled === 'number';
  const sources = [
    { id: 'all', label: 'Tutti' },
    { id: 'ai_advisor', label: 'AI Advisor' },
    { id: 'value_engine', label: 'Value Engine' },
  ];
  const periodLabel = days === 0 ? 'oggi' : `${days}g`;

  return (
    <div
      data-testid="loss-autopsy-card"
      className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4"
    >
      <div className="flex items-center justify-between mb-3 flex-wrap gap-3">
        <div className="flex items-center gap-2 text-zinc-400 text-xs uppercase tracking-wider font-semibold">
          <Microscope size={14} />
          Loss Autopsy — dove bruciamo bankroll ({periodLabel})
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 bg-zinc-800/60 rounded-lg p-1">
            {sources.map((s) => (
              <button
                key={s.id}
                data-testid={`autopsy-source-${s.id}`}
                onClick={() => setSource(s.id)}
                className={`px-2 py-1 text-[11px] rounded-md transition ${
                  source === s.id
                    ? 'bg-zinc-700 text-white font-medium'
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
              >
                {s.label}
              </button>
            ))}
          </div>
          <button
            data-testid="run-autopsy-btn"
            onClick={onRun}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-fuchsia-500/20 hover:bg-fuchsia-500/30 border border-fuchsia-500/30 text-fuchsia-300 text-xs font-medium disabled:opacity-50 transition"
          >
            {loading ? <Loader2 size={13} className="animate-spin" /> : <Microscope size={13} />}
            {loading ? 'Analisi in corso...' : hasData ? 'Rianalizza' : 'Analizza perdite'}
          </button>
        </div>
      </div>

      {!autopsy && !loading && (
        <p className="text-zinc-500 text-sm py-6 text-center">
          Clicca <span className="text-fuchsia-300">Analizza perdite</span> per cercare pattern nei TAKE settled ({periodLabel}).
        </p>
      )}

      {autopsy?.error && (
        <p className="text-red-400 text-sm">Errore: {autopsy.error}</p>
      )}

      {hasData && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-4 text-xs">
            <MetaStat label="Settled" value={autopsy.total_settled} />
            <MetaStat label="W / L" value={`${autopsy.total_wins} / ${autopsy.total_losses}`} />
            <MetaStat label="WR globale" value={`${autopsy.global_wr_pct}%`} />
            <MetaStat
              label="ROI globale"
              value={`${autopsy.global_roi_pct >= 0 ? '+' : ''}${autopsy.global_roi_pct}%`}
              color={autopsy.global_roi_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}
            />
            <MetaStat
              label="PnL"
              value={`${autopsy.global_pnl >= 0 ? '+' : ''}${autopsy.global_pnl}u`}
              color={autopsy.global_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}
            />
          </div>

          {autopsy.total_settled < 10 && (
            <p className="text-amber-400 text-xs mb-3">
              ⚠️ Sample piccolo ({autopsy.total_settled} pick): risultati indicativi.
            </p>
          )}

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <BucketList
              testId="autopsy-bleeding"
              title="Sanguinanti"
              subtitle="Dove stiamo perdendo"
              icon={Droplet}
              tone="red"
              rows={autopsy.bleeding}
            />
            <BucketList
              testId="autopsy-observe"
              title="In osservazione"
              subtitle="Sample piccolo o negativo marginale"
              icon={Eye}
              tone="amber"
              rows={autopsy.observe}
            />
            <BucketList
              testId="autopsy-solid"
              title="Solidi"
              subtitle="Ciò che sta tenendo il book"
              icon={ShieldCheck}
              tone="emerald"
              rows={autopsy.solid}
            />
          </div>
        </>
      )}
    </div>
  );
}

function MetaStat({ label, value, color = 'text-zinc-200' }) {
  return (
    <div className="bg-zinc-800/40 rounded-lg px-3 py-2 border border-zinc-800">
      <div className="text-[10px] uppercase text-zinc-500">{label}</div>
      <div className={`font-mono font-semibold ${color}`}>{value}</div>
    </div>
  );
}

function BucketList({ testId, title, subtitle, icon: Icon, tone, rows }) {
  const toneCfg = {
    red: {
      border: 'border-red-500/20', bg: 'bg-red-500/5',
      badgeBg: 'bg-red-500/15', badgeText: 'text-red-400',
      iconColor: 'text-red-400',
    },
    amber: {
      border: 'border-amber-500/20', bg: 'bg-amber-500/5',
      badgeBg: 'bg-amber-500/15', badgeText: 'text-amber-400',
      iconColor: 'text-amber-400',
    },
    emerald: {
      border: 'border-emerald-500/20', bg: 'bg-emerald-500/5',
      badgeBg: 'bg-emerald-500/15', badgeText: 'text-emerald-400',
      iconColor: 'text-emerald-400',
    },
  }[tone];
  const safeRows = Array.isArray(rows) ? rows : [];
  return (
    <div
      data-testid={testId}
      className={`rounded-lg border ${toneCfg.border} ${toneCfg.bg} p-3`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} className={toneCfg.iconColor} />
        <div>
          <div className="text-sm font-semibold text-zinc-200">{title}</div>
          <div className="text-[10px] text-zinc-500">{subtitle}</div>
        </div>
        <span className={`ml-auto px-1.5 py-0.5 text-[10px] font-bold rounded ${toneCfg.badgeBg} ${toneCfg.badgeText}`}>
          {safeRows.length}
        </span>
      </div>
      {safeRows.length === 0 ? (
        <p className="text-zinc-600 text-xs py-3 text-center">Nessun bucket</p>
      ) : (
        <div className="space-y-1.5 max-h-[480px] overflow-y-auto pr-1">
          {safeRows.map((r, i) => (
            <BucketRow key={i} row={r} tone={tone} />
          ))}
        </div>
      )}
    </div>
  );
}

function BucketRow({ row, tone }) {
  const pnlColor = row.pnl >= 0 ? 'text-emerald-400' : 'text-red-400';
  const roiColor = row.roi_pct >= 0 ? 'text-emerald-400' : 'text-red-400';
  const dimCount = (row.dimensions || []).length;
  return (
    <div className="bg-zinc-900/60 rounded-md p-2 text-xs border border-zinc-800/80">
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="font-mono text-[11px] text-zinc-300 break-all leading-snug">
          {row.label}
        </div>
        <span className="shrink-0 px-1.5 py-0.5 text-[9px] font-bold rounded bg-zinc-800 text-zinc-500">
          {dimCount}D
        </span>
      </div>
      <div className="flex items-center justify-between flex-wrap gap-1 text-[11px] tabular-nums">
        <span className="text-zinc-400">N={row.n}</span>
        <span className="text-zinc-400">
          <span className="text-emerald-400">{row.wins}W</span>
          {' / '}
          <span className="text-red-400">{row.losses}L</span>
          {' · '}WR {row.wr_pct}%
        </span>
        <span className={roiColor}>
          ROI {row.roi_pct >= 0 ? '+' : ''}{row.roi_pct}%
        </span>
        <span className={pnlColor + ' font-semibold'}>
          {row.pnl >= 0 ? '+' : ''}{row.pnl}u
        </span>
      </div>
      <div className="flex items-center justify-between text-[10px] text-zinc-500 mt-0.5">
        <span>lift {row.lift_roi_pp >= 0 ? '+' : ''}{row.lift_roi_pp}pp vs media</span>
        {tone === 'red' && (
          <span className="text-red-400/80">{row.support_loss_pct}% dei loss</span>
        )}
      </div>
    </div>
  );
}

function PicksTab({ picks }) {
  if (!picks.length) return <p className="text-zinc-500 text-center py-10">Nessun pick analizzato nel periodo.</p>;

  return (
    <div className="space-y-2">
      {picks.map((p, i) => {
        const Icon = VERDICT_ICONS[p.verdict] || ShieldAlert;
        const colors = VERDICT_COLORS[p.verdict] || 'text-zinc-400 bg-zinc-500/10 border-zinc-500/30';
        return (
          <div key={i} className="bg-zinc-900/80 rounded-lg border border-zinc-800 p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`px-2.5 py-1 rounded-md border text-xs font-bold ${colors}`}>
                  <Icon size={12} className="inline mr-1" />
                  {p.verdict}
                </span>
                <div>
                  <span className="text-white font-medium text-sm">{p.player1} vs {p.player2}</span>
                  <span className="text-zinc-500 text-xs ml-2">{p.tournament}</span>
                </div>
              </div>
              <div className="flex items-center gap-4 text-xs">
                {p.best_market && p.best_market !== 'none' && (
                  <span className="text-zinc-400 bg-zinc-800 px-2 py-0.5 rounded">
                    {p.best_market === 'match_winner' ? 'MW' : p.best_market === 'handicap' ? 'HC' : p.best_market === 'over_under' ? 'O/U' : p.best_market}
                  </span>
                )}
                <span className="text-violet-400">AI: {p.confidence}/10</span>
                {p.match_result === 'finished' ? (
                  <span className={`font-bold ${p.winner ? 'text-emerald-400' : 'text-red-400'}`}>
                    {p.score}
                  </span>
                ) : p.match_result === 'live' ? (
                  <span className="flex items-center gap-1 text-amber-400 font-medium">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                    LIVE
                  </span>
                ) : p.match_result === 'expired' ? (
                  <span className="text-zinc-600">expired</span>
                ) : p.match_result === 'scheduled' ? (
                  <span className="text-blue-400/80">pre-match</span>
                ) : (
                  <span className="text-zinc-600">{p.score || 'pending'}</span>
                )}
                {p.outcome && p.verdict === 'TAKE' && (
                  <span
                    className={`px-2 py-0.5 rounded font-bold text-[11px] ${
                      p.outcome.void ? 'bg-zinc-700/60 text-zinc-300' :
                      p.outcome.won ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'
                    }`}
                    title={`${p.outcome.selection} @${p.outcome.odds}`}
                  >
                    {p.outcome.void ? 'VOID' : p.outcome.won ? `WIN +${p.outcome.profit}` : `LOSS ${p.outcome.profit}`}
                  </span>
                )}
                <span className="text-zinc-600 w-28 text-right">{(p.created_at || '').replace('T', ' ').slice(0, 16)}</span>
              </div>
            </div>
            {p.key_insight && (
              <p className="text-xs text-zinc-400 mt-2 ml-16">{p.key_insight}</p>
            )}
            {p.risk_flags && p.risk_flags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2 ml-16">
                {p.risk_flags.slice(0, 3).map((f, j) => (
                  <span key={j} className="text-xs text-red-400/70 bg-red-500/5 px-2 py-0.5 rounded">{f}</span>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ReviewTab({ reviews, systemReview }) {
  const allReviews = systemReview ? [{ result: systemReview, created_at: new Date().toISOString() }, ...reviews] : reviews;

  if (!allReviews.length) return (
    <p className="text-zinc-500 text-center py-10">Nessuna review. Clicca "System Review" per generarne una.</p>
  );

  return (
    <div className="space-y-4">
      {allReviews.map((r, i) => {
        const rev = r.result || r;
        if (rev.error) return (
          <div key={i} className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400 text-sm">Errore: {rev.error}</div>
        );

        const grade = rev.overall_grade || '?';
        const gradeColor = GRADE_COLORS[grade] || 'text-zinc-400';

        return (
          <div key={i} className="bg-zinc-900/80 rounded-xl border border-zinc-800 p-5 space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className={`text-3xl font-black ${gradeColor}`}>{grade}</span>
                <div>
                  <h3 className="text-white font-semibold text-sm">System Review</h3>
                  <p className="text-zinc-500 text-xs">{(r.created_at || '').replace('T', ' ').slice(0, 16)}</p>
                </div>
              </div>
            </div>

            {rev.summary && <p className="text-sm text-zinc-300">{rev.summary}</p>}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {rev.strengths && rev.strengths.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-emerald-400 uppercase tracking-wider mb-2">Punti di Forza</h4>
                  {rev.strengths.map((s, j) => (
                    <div key={j} className="flex items-start gap-2 text-sm text-zinc-300 mb-1">
                      <CheckCircle size={13} className="text-emerald-400 mt-0.5 shrink-0" /> {s}
                    </div>
                  ))}
                </div>
              )}
              {rev.weaknesses && rev.weaknesses.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-2">Punti Deboli</h4>
                  {rev.weaknesses.map((w, j) => (
                    <div key={j} className="flex items-start gap-2 text-sm text-zinc-300 mb-1">
                      <XCircle size={13} className="text-red-400 mt-0.5 shrink-0" /> {w}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {rev.recommendations && rev.recommendations.length > 0 && (
              <div>
                <h4 className="text-xs font-semibold text-violet-400 uppercase tracking-wider mb-2">Raccomandazioni</h4>
                {rev.recommendations.map((rec, j) => (
                  <div key={j} className="bg-zinc-800/50 rounded-lg p-3 mb-2 border border-zinc-700/50">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${rec.priority === 'alta' ? 'bg-red-500/20 text-red-400' : rec.priority === 'media' ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-500/20 text-zinc-400'}`}>
                        {rec.priority?.toUpperCase()}
                      </span>
                      <span className="text-sm text-white font-medium">{rec.action}</span>
                    </div>
                    <p className="text-xs text-zinc-400 ml-14">{rec.reason}</p>
                  </div>
                ))}
              </div>
            )}

            {rev.market_advice && (
              <div className="grid grid-cols-3 gap-3">
                {Object.entries(rev.market_advice).map(([mkt, advice]) => (
                  <div key={mkt} className="bg-zinc-800/30 rounded-lg p-3 border border-zinc-800">
                    <span className="text-xs font-bold text-zinc-400 uppercase">{mkt}</span>
                    <p className="text-xs text-zinc-300 mt-1">{advice}</p>
                  </div>
                ))}
              </div>
            )}

            {rev.bankroll_advice && (
              <div className="bg-violet-500/5 border border-violet-500/20 rounded-lg p-3">
                <span className="text-xs font-bold text-violet-400 uppercase">Bankroll</span>
                <p className="text-sm text-zinc-300 mt-1">{rev.bankroll_advice}</p>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


function PatternTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(7);

  const run = async () => {
    setLoading(true);
    try {
      await apiPost(`/tt/admin/ai-pattern-detective?days=${days}`);
      const poll = async () => {
        for (let i = 0; i < 30; i++) {
          await new Promise(r => setTimeout(r, 4000));
          try {
            const s = await apiGet('/tt/admin/ai-pattern-detective');
            if (!s.running && s.last_result) { setData(s.last_result); setLoading(false); return; }
          } catch { /* continue */ }
        }
        setLoading(false);
      };
      poll();
    } catch (e) { setData({ error: e.message }); setLoading(false); }
  };

  // Load cached on mount
  useEffect(() => {
    apiGet('/tt/admin/ai-pattern-detective').then(s => { if (s.last_result) setData(s.last_result); }).catch(() => {});
  }, []);

  const ai = data?.ai_interpretation || {};
  const discoveries = data?.discoveries || [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <select value={days} onChange={e => setDays(Number(e.target.value))} className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-zinc-300">
          <option value={3}>3 giorni</option><option value={7}>7 giorni</option><option value={14}>14 giorni</option><option value={30}>30 giorni</option>
        </select>
        <button onClick={run} disabled={loading} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium disabled:opacity-50">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <BarChart3 size={14} />}
          {loading ? 'Analisi in corso...' : 'Analizza Pattern'}
        </button>
      </div>

      {!data && !loading && <p className="text-zinc-500 text-center py-10">Clicca "Analizza Pattern" per scoprire pattern nascosti.</p>}
      {loading && <div className="flex items-center gap-3 py-10 justify-center"><Loader2 size={20} className="animate-spin text-violet-400" /><span className="text-zinc-400">Analisi in corso...</span></div>}

      {data && !loading && !data.error && (
        <>
          {ai.summary && <div className="bg-violet-500/5 border border-violet-500/20 rounded-lg p-4 text-sm text-zinc-300">{ai.summary}</div>}

          {discoveries.length > 0 && (
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider">Scoperte</h4>
              {discoveries.map((d, i) => (
                <div key={i} className={`p-3 rounded-lg border text-sm ${d.type === 'positive' ? 'bg-emerald-500/5 border-emerald-500/20 text-emerald-300' : 'bg-red-500/5 border-red-500/20 text-red-300'}`}>
                  {d.type === 'positive' ? <CheckCircle size={13} className="inline mr-2" /> : <XCircle size={13} className="inline mr-2" />}
                  {d.msg}
                </div>
              ))}
            </div>
          )}

          {ai.actionable_insights && (
            <div>
              <h4 className="text-xs font-semibold text-violet-400 uppercase tracking-wider mb-2">Azioni Consigliate</h4>
              {ai.actionable_insights.map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-zinc-300 mb-1.5">
                  <TrendingUp size={13} className="text-violet-400 mt-0.5 shrink-0" /> {a}
                </div>
              ))}
            </div>
          )}

          {data.patterns && Object.entries(data.patterns).filter(([k]) => k !== 'streaks').map(([cat, groups]) => (
            <div key={cat} className="bg-zinc-900/80 rounded-lg border border-zinc-800 p-4">
              <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">{cat.replace('by_', '').replace('_', ' ')}</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                {Object.entries(groups).map(([label, stats]) => (
                  <div key={label} className={`p-2 rounded border ${stats.ROI > 5 ? 'border-emerald-500/20 bg-emerald-500/5' : stats.ROI < -10 ? 'border-red-500/20 bg-red-500/5' : 'border-zinc-700 bg-zinc-800/50'}`}>
                    <div className="text-zinc-400 truncate">{label}</div>
                    <div className="text-white font-bold">{stats.n} pick</div>
                    <div className={stats.ROI > 0 ? 'text-emerald-400' : 'text-red-400'}>ROI {stats.ROI}% | PnL {stats.PnL}</div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </>
      )}
      {data?.error && <div className="text-red-400 text-sm p-3 bg-red-500/10 rounded-lg border border-red-500/20">Errore: {data.error}</div>}
    </div>
  );
}

const PATTERN_CLASS_STYLE = {
  STRONG_PATTERN: { label: 'STRONG', bg: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40' },
  BALANCED_PATTERN: { label: 'BALANCED', bg: 'bg-sky-500/15 text-sky-300 border-sky-500/40' },
  WEAK_PATTERN: { label: 'WEAK', bg: 'bg-amber-500/15 text-amber-300 border-amber-500/40' },
  NEGATIVE_PATTERN: { label: 'NEGATIVE', bg: 'bg-red-500/15 text-red-300 border-red-500/40' },
};

const ACTION_STYLE = {
  PASS: { label: 'PASS', bg: 'bg-zinc-700/50 text-zinc-400', emoji: '·' },
  WARN_POSITIVE: { label: 'WARN+', bg: 'bg-emerald-500/15 text-emerald-300', emoji: '↑' },
  WARN_NEGATIVE: { label: 'WARN−', bg: 'bg-amber-500/15 text-amber-300', emoji: '↓' },
  BOOST: { label: 'BOOST', bg: 'bg-fuchsia-500/20 text-fuchsia-300', emoji: '★' },
  BLOCK: { label: 'BLOCK', bg: 'bg-red-500/20 text-red-300', emoji: '⛔' },
};

function PatternGateTab() {
  const [registry, setRegistry] = useState(null);
  const [logs, setLogs] = useState(null);
  const [config, setConfig] = useState(null);
  const [runs, setRuns] = useState(null);
  const [loading, setLoading] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [message, setMessage] = useState(null);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [reg, lg, cfg, rn] = await Promise.all([
        apiGet('/tt/admin/pattern-gate/registry'),
        apiGet('/tt/admin/pattern-gate/logs?hours=24'),
        apiGet('/tt/admin/pattern-gate/config'),
        apiGet('/tt/admin/pattern-gate/runs'),
      ]);
      setRegistry(reg);
      setLogs(lg);
      setConfig(cfg);
      setRuns(rn);
    } catch (e) {
      setMessage(`Errore: ${e.message}`);
    }
    setLoading(false);
  };

  useEffect(() => { loadAll(); }, []);

  const runDiscovery = async () => {
    setDiscovering(true);
    setMessage(null);
    try {
      const r = await apiPost('/tt/admin/pattern-gate/discover');
      if (r?.status === 'ok') {
        setMessage(`Discovery OK: ${r.n_patterns_saved} pattern salvati da ${r.n_picks_loaded} pick (${r.lookback_days}gg)`);
      } else if (r?.status === 'insufficient_data') {
        setMessage(`Dati insufficienti: ${r.n_picks_loaded} pick (min ${r.min_required})`);
      } else {
        setMessage('Discovery completata');
      }
      await loadAll();
    } catch (e) {
      setMessage(`Errore: ${e.message}`);
    }
    setDiscovering(false);
    setTimeout(() => setMessage(null), 10000);
  };

  const toggleConfig = async (key, value) => {
    try {
      await apiPost('/tt/admin/pattern-gate/config', { [key]: value });
      await loadAll();
    } catch (e) {
      setMessage(`Errore config: ${e.message}`);
    }
  };

  if (loading && !registry) {
    return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-zinc-500" /></div>;
  }

  const cfg = config?.config || {};
  const patterns = registry?.patterns || [];
  const logsData = logs?.logs || [];
  const counts = logs?.counts_by_action || {};
  const lastRun = runs?.runs?.[0];

  return (
    <div className="space-y-5" data-testid="pattern-gate-tab">
      <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 p-5 space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <Sparkles size={16} className="text-violet-400" />
              Pattern Gate — Quality Layer
            </h3>
            <p className="text-xs text-zinc-500 mt-1">
              Filtro conservativo sopra il sistema. PASS default, BLOCK richiede evidenza forte (n≥{cfg.min_sample_block || 80}, ROI≤{cfg.block_roi_threshold ?? -20}%).
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="pattern-gate-discover-btn"
              onClick={runDiscovery}
              disabled={discovering}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium disabled:opacity-50"
            >
              {discovering ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              Run Discovery
            </button>
            <button
              onClick={loadAll}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-sm border border-zinc-700"
            >
              <RefreshCw size={14} />
              Ricarica
            </button>
          </div>
        </div>

        {message && (
          <div className="text-xs text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
            {message}
          </div>
        )}

        <div className="flex flex-wrap gap-2 items-center text-xs">
          <Toggle
            label="Enabled"
            value={!!cfg.enabled}
            onChange={(v) => toggleConfig('enabled', v)}
            description={cfg.enabled ? 'Gate attivo (può imporre BLOCK)' : 'Gate disattivato'}
          />
          <Toggle
            label="Shadow Mode"
            value={!!cfg.shadow_mode}
            onChange={(v) => toggleConfig('shadow_mode', v)}
            description={cfg.shadow_mode ? 'Logga ma NON blocca' : 'LIVE — enforcing'}
            yellow={cfg.shadow_mode}
          />
          {lastRun && (
            <span className="ml-auto text-zinc-500 text-[11px]">
              Ultima discovery: {new Date(lastRun.started_at).toLocaleString('it-IT')} • {lastRun.n_patterns_saved || 0} pattern da {lastRun.n_picks_loaded || 0} pick
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {['PASS', 'WARN_POSITIVE', 'WARN_NEGATIVE', 'BOOST', 'BLOCK'].map(act => {
          const st = ACTION_STYLE[act];
          const n = counts[act] || 0;
          return (
            <div key={act} className="bg-zinc-900/80 border border-zinc-800 rounded-xl p-4">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">{st.label} (24h)</div>
              <div className={`text-2xl font-bold tabular-nums ${st.bg.split(' ')[1]}`}>{n}</div>
            </div>
          );
        })}
      </div>

      <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <h4 className="text-sm font-semibold text-white">Pattern validati</h4>
            <p className="text-[11px] text-zinc-500">{patterns.length} pattern • ordinati per impatto (NEGATIVE prima, poi score)</p>
          </div>
        </div>
        {patterns.length === 0 ? (
          <div className="p-10 text-center text-zinc-500 text-sm">
            Nessun pattern validato. Lancia "Run Discovery" per analizzare lo storico.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-950/60 text-zinc-500 uppercase tracking-wider">
                <tr>
                  <th className="px-4 py-2 text-left">Tipo</th>
                  <th className="px-4 py-2 text-left">Filtro</th>
                  <th className="px-3 py-2 text-right">N</th>
                  <th className="px-3 py-2 text-right">WR</th>
                  <th className="px-3 py-2 text-right">WR_w</th>
                  <th className="px-3 py-2 text-right">ROI</th>
                  <th className="px-3 py-2 text-right">PnL</th>
                  <th className="px-3 py-2 text-right">Stab</th>
                  <th className="px-3 py-2 text-right">Score</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60">
                {patterns.map((p, i) => {
                  const st = PATTERN_CLASS_STYLE[p.classification] || PATTERN_CLASS_STYLE.WEAK_PATTERN;
                  const roi = p.stats?.roi_pct ?? 0;
                  const wr = p.stats?.wr ?? 0;
                  const wrW = p.stats?.wr_wilson_lower ?? 0;
                  return (
                    <tr key={i} className="hover:bg-zinc-800/30">
                      <td className="px-4 py-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold border ${st.bg}`}>{st.label}</span>
                      </td>
                      <td className="px-4 py-2 font-mono text-zinc-400">
                        {Object.entries(p.dims || {}).map(([k, v]) => (
                          <span key={k} className="inline-block mr-2">
                            <span className="text-zinc-600">{k}=</span>
                            <span className="text-zinc-300">{v}</span>
                          </span>
                        ))}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-300">{p.stats?.n}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-400">{(wr * 100).toFixed(1)}%</td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-500">{(wrW * 100).toFixed(0)}%</td>
                      <td className={`px-3 py-2 text-right tabular-nums font-semibold ${roi >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                        {roi >= 0 ? '+' : ''}{roi.toFixed(1)}%
                      </td>
                      <td className={`px-3 py-2 text-right tabular-nums ${p.stats?.pnl >= 0 ? 'text-emerald-400/80' : 'text-red-400/80'}`}>
                        {p.stats?.pnl >= 0 ? '+' : ''}{(p.stats?.pnl ?? 0).toFixed(1)}u
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-400">{(p.stability ?? 0).toFixed(2)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-violet-300 font-semibold">{(p.score ?? 0).toFixed(2)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 overflow-hidden">
        <div className="px-5 py-3 border-b border-zinc-800">
          <h4 className="text-sm font-semibold text-white">Decisioni recenti (ultime 24h)</h4>
          <p className="text-[11px] text-zinc-500">{logsData.length} eventi • {logs?.enforced_blocks || 0} block effettivamente imposti</p>
        </div>
        {logsData.length === 0 ? (
          <div className="p-8 text-center text-zinc-500 text-sm">Nessuna decisione registrata nelle ultime 24h.</div>
        ) : (
          <div className="max-h-[480px] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-950/60 text-zinc-500 uppercase tracking-wider sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left">Ora</th>
                  <th className="px-3 py-2 text-left">Azione</th>
                  <th className="px-3 py-2 text-left">Mercato</th>
                  <th className="px-3 py-2 text-left">Selezione</th>
                  <th className="px-3 py-2 text-right">EV</th>
                  <th className="px-3 py-2 text-right">Odds</th>
                  <th className="px-3 py-2 text-left">Pattern</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-800/60">
                {logsData.map((l, i) => {
                  const st = ACTION_STYLE[l.decision?.action] || ACTION_STYLE.PASS;
                  const enforced = l.decision?.enforced;
                  return (
                    <tr key={i} className="hover:bg-zinc-800/30">
                      <td className="px-4 py-2 text-zinc-500 whitespace-nowrap">
                        {l.created_at ? new Date(l.created_at).toLocaleTimeString('it-IT') : '—'}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-semibold ${st.bg}`}>
                          {st.emoji} {st.label}
                        </span>
                        {enforced && <span className="ml-1 text-[9px] text-red-300 uppercase">live</span>}
                      </td>
                      <td className="px-3 py-2 text-zinc-400">{l.market_type || '—'}</td>
                      <td className="px-3 py-2 text-zinc-300 max-w-[180px] truncate">{l.selection || '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-400">{l.ev_pct ? `${l.ev_pct.toFixed(1)}%` : '—'}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-zinc-500">{l.bookmaker_odds ?? '—'}</td>
                      <td className="px-3 py-2 text-zinc-500 font-mono text-[10px] truncate max-w-[250px]">
                        {l.decision?.pattern_key || <span className="text-zinc-700">no-match</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function Toggle({ label, value, onChange, description, yellow }) {
  return (
    <label className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition ${value ? (yellow ? 'border-amber-500/40 bg-amber-500/10' : 'border-emerald-500/40 bg-emerald-500/10') : 'border-zinc-800 bg-zinc-900/60'}`}>
      <input
        type="checkbox"
        checked={value}
        onChange={e => onChange(e.target.checked)}
        className="w-3.5 h-3.5 accent-violet-500"
        data-testid={`pattern-gate-toggle-${label.toLowerCase().replace(/\s/g,'-')}`}
      />
      <span className="font-medium text-zinc-200">{label}</span>
      {description && <span className="text-[10px] text-zinc-500">— {description}</span>}
    </label>
  );
}


function CalibratorTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      await apiPost('/tt/admin/ai-model-calibrator?days=14');
      const poll = async () => {
        for (let i = 0; i < 30; i++) {
          await new Promise(r => setTimeout(r, 4000));
          try {
            const s = await apiGet('/tt/admin/ai-model-calibrator');
            if (!s.running && s.last_result) { setData(s.last_result); setLoading(false); return; }
          } catch { /* continue */ }
        }
        setLoading(false);
      };
      poll();
    } catch (e) { setData({ error: e.message }); setLoading(false); }
  };

  useEffect(() => {
    apiGet('/tt/admin/ai-model-calibrator').then(s => { if (s.last_result) setData(s.last_result); }).catch(() => {});
  }, []);

  const ai = data?.ai_analysis || {};

  return (
    <div className="space-y-4">
      <button onClick={run} disabled={loading} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium disabled:opacity-50">
        {loading ? <Loader2 size={14} className="animate-spin" /> : <BarChart3 size={14} />}
        {loading ? 'Calibrazione in corso...' : 'Analizza Calibrazione'}
      </button>

      {!data && !loading && <p className="text-zinc-500 text-center py-10">Clicca per verificare la calibrazione dei modelli.</p>}
      {loading && <div className="flex items-center gap-3 py-10 justify-center"><Loader2 size={20} className="animate-spin text-violet-400" /><span className="text-zinc-400">Analisi calibrazione...</span></div>}

      {data && !loading && !data.error && (
        <>
          <div className="flex items-center gap-4">
            {ai.overall_grade && <span className={`text-4xl font-black ${{'A':'text-emerald-400','B':'text-green-400','C':'text-amber-400','D':'text-orange-400','F':'text-red-400'}[ai.overall_grade] || 'text-zinc-400'}`}>{ai.overall_grade}</span>}
            <div>
              <h3 className="text-white font-semibold">Calibrazione Modelli</h3>
              {ai.summary && <p className="text-sm text-zinc-400">{ai.summary}</p>}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {ai.pascal_assessment && <div className="bg-zinc-900/80 rounded-lg border border-zinc-800 p-4"><h4 className="text-xs font-bold text-blue-400 uppercase mb-1">Pascal</h4><p className="text-sm text-zinc-300">{ai.pascal_assessment}</p></div>}
            {ai.lr_assessment && <div className="bg-zinc-900/80 rounded-lg border border-zinc-800 p-4"><h4 className="text-xs font-bold text-orange-400 uppercase mb-1">LR</h4><p className="text-sm text-zinc-300">{ai.lr_assessment}</p></div>}
            {ai.market_comparison && <div className="bg-zinc-900/80 rounded-lg border border-zinc-800 p-4"><h4 className="text-xs font-bold text-emerald-400 uppercase mb-1">vs Mercato</h4><p className="text-sm text-zinc-300">{ai.market_comparison}</p></div>}
          </div>

          {ai.bias_warnings && ai.bias_warnings.length > 0 && (
            <div><h4 className="text-xs font-semibold text-red-400 uppercase tracking-wider mb-2">Bias Rilevati</h4>
              {ai.bias_warnings.map((w, i) => <div key={i} className="flex items-start gap-2 text-sm text-red-300 mb-1"><ShieldAlert size={13} className="mt-0.5 shrink-0" />{w}</div>)}
            </div>
          )}

          {ai.recommendations && (
            <div><h4 className="text-xs font-semibold text-violet-400 uppercase tracking-wider mb-2">Raccomandazioni</h4>
              {ai.recommendations.map((r, i) => (
                <div key={i} className="bg-zinc-800/50 rounded-lg p-3 mb-2 border border-zinc-700/50">
                  <span className={`text-xs font-bold px-1.5 py-0.5 rounded mr-2 ${r.priority === 'alta' ? 'bg-red-500/20 text-red-400' : r.priority === 'media' ? 'bg-amber-500/20 text-amber-400' : 'bg-zinc-500/20 text-zinc-400'}`}>{r.priority?.toUpperCase()}</span>
                  <span className="text-sm text-white">{r.action}</span>
                  {r.expected_impact && <p className="text-xs text-zinc-500 mt-1 ml-12">{r.expected_impact}</p>}
                </div>
              ))}
            </div>
          )}

          {/* Calibration curves */}
          {data.pascal_calibration && (
            <div className="bg-zinc-900/80 rounded-lg border border-zinc-800 p-4">
              <h4 className="text-xs font-semibold text-zinc-400 uppercase tracking-wider mb-2">Curva Calibrazione (predicted vs actual)</h4>
              <div className="grid grid-cols-3 gap-3 text-xs">
                {['pascal', 'lr', 'market'].map(model => (
                  <div key={model}>
                    <span className="font-bold text-zinc-400 uppercase">{model}</span>
                    {(data[`${model}_calibration`] || []).map((b, i) => (
                      <div key={i} className="flex justify-between py-0.5">
                        <span className="text-zinc-500">{b.range}</span>
                        <span className={Math.abs(b.gap) > 0.1 ? 'text-red-400' : 'text-emerald-400'}>{b.actual_wr > 0 ? `${(b.actual_wr*100).toFixed(0)}%` : '-'} ({b.gap > 0 ? '+' : ''}{(b.gap*100).toFixed(0)})</span>
                        <span className="text-zinc-600">n={b.n}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {data?.error && <div className="text-red-400 text-sm p-3 bg-red-500/10 rounded-lg border border-red-500/20">Errore: {data.error}</div>}
    </div>
  );
}


function EdgeFinderTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      await apiPost('/tt/admin/ai-edge-finder?max_matches=10');
      const poll = async () => {
        for (let i = 0; i < 40; i++) {
          await new Promise(r => setTimeout(r, 5000));
          try {
            const s = await apiGet('/tt/admin/ai-edge-finder');
            if (!s.running && s.last_result) { setData(s.last_result); setLoading(false); return; }
          } catch { /* continue */ }
        }
        setLoading(false);
      };
      poll();
    } catch (e) { setData({ error: e.message }); setLoading(false); }
  };

  useEffect(() => {
    apiGet('/tt/admin/ai-edge-finder').then(s => { if (s.last_result) setData(s.last_result); }).catch(() => {});
  }, []);

  const edges = data?.edges || [];
  const EDGE_ICONS = { momentum: '📈', fatigue: '😤', h2h_context: '🔄', market_anomaly: '⚡', close_call: '🎯', ou_trend: '📊', hc_pattern: '📏', none: '❌' };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-500">Cerca valore nascosto dove i modelli non vedono — su MW, O/U e HC</p>
        <button onClick={run} disabled={loading} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium disabled:opacity-50">
          {loading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
          {loading ? 'Scansione...' : 'Cerca Edge Nascosti'}
        </button>
      </div>

      {!data && !loading && <p className="text-zinc-500 text-center py-10">Clicca per scansionare tutti i match e trovare valore nascosto.</p>}
      {loading && (
        <div className="flex flex-col items-center gap-3 py-10">
          <Loader2 size={24} className="animate-spin text-violet-400" />
          <span className="text-zinc-400">Scansione match in corso... l'AI analizza ogni mercato</span>
          <span className="text-zinc-600 text-xs">Potrebbe richiedere 1-2 minuti</span>
        </div>
      )}

      {data && !loading && !data.error && (
        <>
          <div className="grid grid-cols-3 gap-3">
            <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 p-4 text-center">
              <div className="text-2xl font-bold text-white">{data.total_scanned || 0}</div>
              <div className="text-xs text-zinc-500">Match scansionati</div>
            </div>
            <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 p-4 text-center">
              <div className="text-2xl font-bold text-amber-400">{data.candidates_analyzed || 0}</div>
              <div className="text-xs text-zinc-500">Candidati analizzati</div>
            </div>
            <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 p-4 text-center">
              <div className="text-2xl font-bold text-emerald-400">{edges.length}</div>
              <div className="text-xs text-zinc-500">Edge trovati</div>
            </div>
          </div>

          {edges.length === 0 && <p className="text-zinc-500 text-center py-6">Nessun edge nascosto trovato — i modelli coprono tutto.</p>}

          {edges.map((e, i) => (
            <div key={i} className="bg-zinc-900/80 rounded-xl border border-violet-500/20 p-5 space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <span className="text-lg mr-2">{EDGE_ICONS[e.edge_type] || '🔍'}</span>
                  <span className="text-white font-bold">{e.player1} vs {e.player2}</span>
                  <span className="text-zinc-500 text-xs ml-2">{e.tournament}</span>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="px-2 py-1 rounded bg-violet-500/20 text-violet-300 font-bold">{e.edge_type}</span>
                  <span className="text-amber-400">Conf: {e.confidence}/10</span>
                  <span className={`px-2 py-0.5 rounded ${e.risk_level === 'basso' ? 'bg-emerald-500/20 text-emerald-300' : e.risk_level === 'medio' ? 'bg-amber-500/20 text-amber-300' : 'bg-red-500/20 text-red-300'}`}>{e.risk_level}</span>
                </div>
              </div>

              {e.selection && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-emerald-500/5 border border-emerald-500/20">
                  <TrendingUp size={16} className="text-emerald-400 shrink-0" />
                  <span className="text-sm text-emerald-200 font-medium">{e.selection}</span>
                  {e.suggested_stake && <span className="ml-auto text-xs text-zinc-400">{e.suggested_stake}</span>}
                </div>
              )}

              {e.analysis && <p className="text-sm text-zinc-300 leading-relaxed whitespace-pre-line">{e.analysis}</p>}
            </div>
          ))}
        </>
      )}
      {data?.error && <div className="text-red-400 text-sm p-3 bg-red-500/10 rounded-lg border border-red-500/20">Errore: {data.error}</div>}
    </div>
  );
}

function AnomalyTab() {
  const [data, setData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [healing, setHealing] = useState(null);

  const SEV_COLORS = {
    critical: 'bg-red-500/10 border-red-500/30 text-red-300',
    warning: 'bg-amber-500/10 border-amber-500/30 text-amber-300',
    info: 'bg-sky-500/10 border-sky-500/30 text-sky-300',
  };
  const SEV_ICONS = { critical: '❗', warning: '⚠️', info: 'ℹ️' };
  const TYPE_LABELS = {
    stale_odds: 'Quote Stale',
    value_trap: 'Value Trap',
    model_drift: 'Model Drift',
    concentration: 'Concentrazione',
    broken_odds: 'Quote Rotte',
    high_margin: 'Margine Alto',
    confidence_paradox: 'Paradosso Confidence',
  };

  const loadHistory = async () => {
    try {
      const h = await apiGet('/tt/admin/ai-anomaly-history?limit=10');
      setHistory(h?.scans || []);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadHistory(); loadHealing(); }, []);

  const loadHealing = async () => {
    try {
      const h = await apiGet('/tt/admin/self-healing/status');
      setHealing(h);
    } catch { /* ignore */ }
  };

  const toggleHealing = async () => {
    try {
      await apiPost('/tt/admin/self-healing/toggle', { enabled: !healing?.enabled });
      await loadHealing();
    } catch (e) { console.error(e); }
  };

  const run = async () => {
    setLoading(true);
    try {
      const res = await apiPost('/tt/admin/ai-anomaly-scan');
      setData(res);
      await loadHistory();
      await loadHealing();
    } catch (e) {
      setData({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const current = data || (history[0] ? { anomalies: history[0].anomalies, stats: history[0].stats } : null);
  const anomalies = current?.anomalies || [];
  const stats = current?.stats || {};

  return (
    <div className="space-y-4" data-testid="anomaly-monitor-tab">
      <div className="flex items-center justify-between">
        <p className="text-sm text-zinc-500">Monitoraggio anomalie: quote stale, value trap, drift del modello, margini rotti</p>
        <button
          onClick={run}
          disabled={loading}
          data-testid="anomaly-scan-btn"
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium disabled:opacity-50"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <ShieldAlert size={14} />}
          {loading ? 'Scansione...' : 'Esegui Scan'}
        </button>
      </div>

      {!current && !loading && (
        <p className="text-zinc-500 text-center py-10">Nessuna scansione disponibile. Clicca "Esegui Scan" per avviare.</p>
      )}

      {/* Self-Healing panel */}
      {healing && (
        <div className={`rounded-xl border p-4 ${healing.enabled ? 'bg-violet-500/5 border-violet-500/30' : 'bg-zinc-900/80 border-zinc-800'}`}>
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="flex items-center gap-2">
                <ShieldAlert size={16} className={healing.enabled ? 'text-violet-400' : 'text-zinc-500'} />
                <span className="text-sm font-semibold text-zinc-200">Self-Healing</span>
                <span className={`text-xs px-2 py-0.5 rounded-full ${healing.enabled ? 'bg-violet-500/20 text-violet-300' : 'bg-zinc-700/40 text-zinc-400'}`}>
                  {healing.enabled ? 'ATTIVO' : 'DISATTIVATO'}
                </span>
              </div>
              <p className="text-xs text-zinc-500 mt-1">
                Auto-remediation: model drift → auto-recal · stale/broken odds → esclusione picks
                {healing.exclusions?.length > 0 && (
                  <span className="ml-2 text-amber-400">{healing.exclusions.length} match attualmente esclusi</span>
                )}
              </p>
            </div>
            <button
              onClick={toggleHealing}
              data-testid="self-healing-toggle-btn"
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition ${
                healing.enabled
                  ? 'bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/30'
                  : 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/30'
              }`}
            >
              {healing.enabled ? 'Disattiva' : 'Attiva'}
            </button>
          </div>

          {healing.exclusions?.length > 0 && (
            <div className="mt-3 border-t border-zinc-800 pt-3 space-y-1">
              <div className="text-xs text-zinc-500 mb-2">Match esclusi (temporaneo)</div>
              {healing.exclusions.slice(0, 6).map((e, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className="text-zinc-400 truncate flex-1">{e.match_id.replace(/^ttm_/, '').replace(/_/g, ' ').slice(0, 60)}</span>
                  <span className="text-amber-400 font-mono text-[10px]">{e.reason}</span>
                  <span className="text-zinc-600 font-mono text-[10px]">fino {e.expires_at?.slice(11, 16)}</span>
                </div>
              ))}
            </div>
          )}

          {healing.log?.length > 0 && (
            <div className="mt-3 border-t border-zinc-800 pt-3">
              <div className="text-xs text-zinc-500 mb-2">Storico ({healing.log.length} eventi)</div>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {healing.log.slice(0, 6).map((h, i) => (
                  <div key={i} className="text-xs text-zinc-500 flex items-center gap-2">
                    <span className="font-mono tabular-nums">{(h.created_at || '').slice(5, 16).replace('T', ' ')}</span>
                    <span>·</span>
                    <span className="text-zinc-300">{h.actions_count} azioni</span>
                    <span className="text-zinc-600 truncate">({(h.actions || []).map(a => a.action).slice(0, 3).join(', ')})</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {current && (
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 p-4 text-center">
            <div className="text-2xl font-bold text-white">{stats.checks_run || 0}</div>
            <div className="text-xs text-zinc-500">Controlli eseguiti</div>
          </div>
          <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 p-4 text-center">
            <div className="text-2xl font-bold text-amber-400">{stats.anomalies_found || 0}</div>
            <div className="text-xs text-zinc-500">Anomalie rilevate</div>
          </div>
          <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 p-4 text-center">
            <div className="text-2xl font-bold text-red-400">{anomalies.filter(a => a.severity === 'critical').length}</div>
            <div className="text-xs text-zinc-500">Critiche</div>
          </div>
        </div>
      )}

      {current && anomalies.length === 0 && (
        <div className="p-5 rounded-xl bg-emerald-500/5 border border-emerald-500/20 text-center">
          <CheckCircle size={24} className="inline text-emerald-400 mb-2" />
          <p className="text-emerald-300 font-medium">Nessuna anomalia rilevata — sistema stabile</p>
        </div>
      )}

      {anomalies.map((a, i) => (
        <div key={i} className={`rounded-xl border p-4 ${SEV_COLORS[a.severity] || SEV_COLORS.info}`}>
          <div className="flex items-start gap-3">
            <span className="text-lg">{SEV_ICONS[a.severity] || 'ℹ️'}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-bold text-sm">{TYPE_LABELS[a.type] || a.type}</span>
                <span className="text-xs uppercase opacity-60">{a.severity}</span>
              </div>
              {a.match && <div className="text-sm text-zinc-200 font-medium mb-1">{a.match}</div>}
              <p className="text-sm opacity-90 leading-relaxed">{a.detail}</p>
            </div>
          </div>
        </div>
      ))}

      {history.length > 0 && (
        <div className="pt-4 border-t border-zinc-800">
          <p className="text-xs text-zinc-500 mb-2">Ultime scansioni</p>
          <div className="space-y-1">
            {history.slice(0, 5).map((h, i) => (
              <div key={i} className="flex items-center justify-between text-xs text-zinc-400 py-1">
                <span>{h.created_at?.slice(0, 16).replace('T', ' ')}</span>
                <span>{h.stats?.anomalies_found || 0} anomalie · {h.stats?.checks_run || 0} check</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data?.error && <div className="text-red-400 text-sm p-3 bg-red-500/10 rounded-lg border border-red-500/20">Errore: {data.error}</div>}
    </div>
  );
}


function CalibrationTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [days, setDays] = useState(30);
  const [selected, setSelected] = useState('mw_pascal');
  const [active, setActive] = useState(null);
  const [fitting, setFitting] = useState(false);
  const [autoHistory, setAutoHistory] = useState([]);
  const [autoRunning, setAutoRunning] = useState(false);
  const [modelHealth, setModelHealth] = useState(null);
  const [loadingMH, setLoadingMH] = useState(false);

  const load = async (d = days) => {
    setLoading(true);
    try {
      const res = await apiGet(`/tt/admin/calibration-metrics?days=${d}`);
      setData(res);
    } catch (e) {
      setData({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const loadActive = async () => {
    try {
      const a = await apiGet('/tt/admin/calibration/active');
      setActive(a);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(days); }, [days]);
  useEffect(() => { loadActive(); loadAutoHistory(); loadModelHealth(); }, []);

  const loadModelHealth = async () => {
    setLoadingMH(true);
    try {
      const res = await apiGet('/tt/admin/model-health?days=90');
      setModelHealth(res);
    } catch (e) {
      setModelHealth({ error: e.message });
    } finally {
      setLoadingMH(false);
    }
  };

  const loadAutoHistory = async () => {
    try {
      const r = await apiGet('/tt/admin/auto-recalibration-history?limit=10');
      setAutoHistory(r?.history || []);
    } catch { /* ignore */ }
  };

  const runAutoRecal = async (force = true) => {
    setAutoRunning(true);
    try {
      await apiPost(`/tt/admin/auto-recalibration?force=${force}`);
      for (let i = 0; i < 20; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const r = await apiGet('/tt/admin/auto-recalibration');
        if (!r?.running) { await loadAutoHistory(); await loadActive(); break; }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setAutoRunning(false);
    }
  };

  const fitCalibration = async () => {
    setFitting(true);
    try {
      await apiPost('/tt/admin/calibration/fit?days=90');
      await loadActive();
    } catch (e) {
      console.error(e);
    } finally {
      setFitting(false);
    }
  };

  const toggleCalibration = async () => {
    try {
      const enabled = !active?.enabled;
      await apiPost('/tt/admin/calibration/toggle', { enabled });
      await loadActive();
    } catch (e) {
      console.error(e);
    }
  };

  const LABELS = { mw_pascal: 'MW Pascal', mw_lr: 'MW LR', ou: 'O/U', hc: 'HC' };
  const current = data?.[selected];

  const getBadge = (metric, value) => {
    if (value === null || value === undefined) return 'text-zinc-500';
    if (metric === 'ece') {
      if (value < 0.04) return 'text-emerald-400';
      if (value < 0.08) return 'text-amber-400';
      return 'text-red-400';
    }
    if (metric === 'bss') {
      if (value > 0.05) return 'text-emerald-400';
      if (value > 0) return 'text-amber-400';
      return 'text-red-400';
    }
    return 'text-zinc-300';
  };

  return (
    <div className="space-y-4" data-testid="calibration-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-zinc-300 font-medium">Calibration Monitor — Metriche probabilistiche</p>
          <p className="text-xs text-zinc-500 mt-0.5">Brier · ECE · LogLoss · Reliability diagram su predizioni settate</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={days}
            onChange={e => setDays(Number(e.target.value))}
            className="bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-1.5 text-sm text-zinc-300"
            data-testid="calibration-days-select"
          >
            <option value={7}>7 giorni</option>
            <option value={30}>30 giorni</option>
            <option value={90}>90 giorni</option>
            <option value={365}>1 anno</option>
          </select>
          <button
            onClick={() => load(days)}
            disabled={loading}
            data-testid="calibration-refresh-btn"
            className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            Aggiorna
          </button>
        </div>
      </div>

      {/* Model selector tabs */}
      <div className="flex gap-2 flex-wrap">
        {Object.entries(LABELS).map(([key, label]) => {
          const m = data?.[key];
          const ece = m?.ece;
          const active = selected === key;
          return (
            <button
              key={key}
              onClick={() => setSelected(key)}
              data-testid={`calibration-model-${key}`}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition ${
                active ? 'bg-violet-500/20 border-violet-500/40 text-violet-200' : 'bg-zinc-900/80 border-zinc-800 text-zinc-400 hover:border-zinc-700'
              }`}
            >
              {label}
              {m && !m.insufficient_data && (
                <span className={`ml-2 text-xs ${getBadge('ece', ece)}`}>ECE {(ece * 100).toFixed(1)}%</span>
              )}
            </button>
          );
        })}
      </div>

      {/* Calibration adjuster panel */}
      <div className={`rounded-xl border p-4 ${active?.enabled ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-zinc-900/80 border-zinc-800'}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <ShieldAlert size={16} className={active?.enabled ? 'text-emerald-400' : 'text-zinc-500'} />
              <span className="text-sm font-semibold text-zinc-200">Correzione Isotonic</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${active?.enabled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-zinc-700/40 text-zinc-400'}`}>
                {active?.enabled ? 'ATTIVA' : 'DISATTIVATA'}
              </span>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              Applica correzione monotona (PAV) alle probabilità Pascal & LR per ridurre il bias sistematico.
              {active?.models?.mw_pascal && (
                <span className="ml-2">
                  Pascal: <span className="text-violet-400">{active.models.mw_pascal.n_samples}</span> campioni · correzione media <span className="text-amber-400">{(active.models.mw_pascal.avg_correction * 100).toFixed(1)}pp</span>
                </span>
              )}
              {active?.models?.mw_lr && (
                <span className="ml-2">
                  · LR: <span className="text-violet-400">{active.models.mw_lr.n_samples}</span> · <span className="text-amber-400">{(active.models.mw_lr.avg_correction * 100).toFixed(1)}pp</span>
                </span>
              )}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fitCalibration}
              disabled={fitting}
              data-testid="calibration-fit-btn"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-medium border border-zinc-700 disabled:opacity-50"
            >
              {fitting ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {fitting ? 'Fitting...' : 'Ri-adatta (90gg)'}
            </button>
            <button
              onClick={toggleCalibration}
              disabled={!active?.models || Object.keys(active?.models || {}).length === 0}
              data-testid="calibration-toggle-btn"
              className={`px-4 py-1.5 rounded-lg text-xs font-bold transition ${
                active?.enabled
                  ? 'bg-red-500/20 text-red-300 hover:bg-red-500/30 border border-red-500/30'
                  : 'bg-emerald-500/20 text-emerald-300 hover:bg-emerald-500/30 border border-emerald-500/30'
              } disabled:opacity-50`}
            >
              {active?.enabled ? 'Disattiva' : 'Attiva'}
            </button>
          </div>
        </div>
      </div>

      {/* Model Health Guardian (Layer E) */}
      <div
        className={`rounded-xl border p-4 ${
          modelHealth?.ok === false
            ? 'bg-red-500/5 border-red-500/40'
            : modelHealth?.ok === true
            ? 'bg-emerald-500/5 border-emerald-500/30'
            : 'bg-zinc-900/80 border-zinc-800'
        }`}
        data-testid="model-health-card"
      >
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div>
            <div className="flex items-center gap-2">
              <ShieldAlert
                size={16}
                className={
                  modelHealth?.ok === false ? 'text-red-400'
                  : modelHealth?.ok === true ? 'text-emerald-400'
                  : 'text-zinc-500'
                }
              />
              <span className="text-sm font-semibold text-zinc-200">
                Model Health Guardian (Layer E)
              </span>
              {modelHealth && (
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                    modelHealth.ok
                      ? 'bg-emerald-500/20 text-emerald-300'
                      : 'bg-red-500/20 text-red-300'
                  }`}
                  data-testid="model-health-status"
                >
                  {modelHealth.ok ? 'HEALTHY' : 'ALERT'}
                </span>
              )}
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              Valida la qualità statistica dei modelli di calibrazione + plausibilità base rates + anomalie runtime. Blocca fit di modelli corrotti.
            </p>
          </div>
          <button
            onClick={loadModelHealth}
            disabled={loadingMH}
            data-testid="model-health-refresh-btn"
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-medium border border-zinc-700 disabled:opacity-50"
          >
            {loadingMH ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            Verifica
          </button>
        </div>

        {modelHealth && !modelHealth.error && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* A — Calibration sanity */}
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
              <p className="text-xs font-bold text-zinc-300 mb-2">A · Calibrazione</p>
              <div className="space-y-1">
                {Object.entries(modelHealth.calibration || {}).map(([key, v]) => (
                  <div key={key} className="flex items-center justify-between text-xs">
                    <span className="text-zinc-400">{key}</span>
                    <span
                      className={v.ok ? 'text-emerald-400' : 'text-red-400'}
                      data-testid={`model-health-cal-${key}`}
                    >
                      {v.ok ? '✓ ok' : `✗ ${v.violations?.[0]?.split(':')[0] || 'fail'}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* B — Base rates */}
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
              <p className="text-xs font-bold text-zinc-300 mb-2">B · Base Rates</p>
              <div className="flex items-center justify-between text-xs mb-1">
                <span className="text-zinc-400">n match</span>
                <span className="text-zinc-300">{modelHealth.base_rates?.n_matches ?? '?'}</span>
              </div>
              {Object.entries(modelHealth.base_rates?.rates || {})
                .filter(([k]) => k.startsWith('HC_'))
                .map(([k, r]) => {
                  const [lo, hi] = r.plausible_range || [0, 1];
                  const within = r.rate >= lo && r.rate <= hi;
                  return (
                    <div key={k} className="flex items-center justify-between text-xs">
                      <span className="text-zinc-400">{k.replace('HC_', 'HC ')}</span>
                      <span className={within ? 'text-emerald-400' : 'text-red-400'}>
                        {(r.rate * 100).toFixed(1)}%
                      </span>
                    </div>
                  );
                })}
            </div>

            {/* C — Output anomalies */}
            <div className="bg-zinc-900/60 border border-zinc-800 rounded-lg p-3">
              <p className="text-xs font-bold text-zinc-300 mb-2">C · Output Runtime</p>
              {modelHealth.output_anomalies?.ou_counts?.total > 0 && (
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className="text-zinc-400">OU prob healthy</span>
                  <span className="text-emerald-400">
                    {modelHealth.output_anomalies.ou_counts.healthy}/
                    {modelHealth.output_anomalies.ou_counts.total}
                  </span>
                </div>
              )}
              {Object.entries(modelHealth.output_anomalies?.rec_stats_summary || {}).map(([mt, s]) => (
                <div key={mt} className="flex items-center justify-between text-xs">
                  <span className="text-zinc-400">{mt}</span>
                  <span className={s.ev_gt_50 > s.total * 0.25 ? 'text-amber-400' : 'text-zinc-300'}>
                    {s.ev_gt_50}/{s.total} EV&gt;50%
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}

        {modelHealth && !modelHealth.ok && (
          <div className="mt-3 p-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-xs text-red-200" data-testid="model-health-violations">
            <p className="font-bold mb-1">Violazioni rilevate:</p>
            <ul className="space-y-0.5 list-disc pl-4">
              {Object.values(modelHealth.calibration || {})
                .flatMap(c => (c.violations || []).map(v => `${c.model_key}: ${v}`))
                .concat(modelHealth.base_rates?.violations || [])
                .concat(modelHealth.output_anomalies?.violations || [])
                .slice(0, 5)
                .map((v, i) => <li key={i}>{v}</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* Auto-Recalibration loop panel */}
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/80 p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <div className="flex items-center gap-2">
              <RefreshCw size={16} className="text-violet-400" />
              <span className="text-sm font-semibold text-zinc-200">Anello Auto-Recalibrazione</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-violet-500/20 text-violet-300">DAILY 05:45 CET</span>
            </div>
            <p className="text-xs text-zinc-500 mt-1">
              Champion vs challenger con holdout temporale (7gg) — promuove solo se ECE migliora di ≥1pp senza regressione Brier.
            </p>
          </div>
          <button
            onClick={() => runAutoRecal(true)}
            disabled={autoRunning}
            data-testid="auto-recal-btn"
            className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-200 text-xs font-medium border border-zinc-700 disabled:opacity-50"
          >
            {autoRunning ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
            {autoRunning ? 'In esecuzione...' : 'Esegui ora (force)'}
          </button>
        </div>

        {autoHistory.length > 0 && (
          <div className="mt-4 border-t border-zinc-800 pt-3">
            <div className="text-xs text-zinc-500 mb-2">Ultimi {autoHistory.length} report</div>
            <div className="space-y-2">
              {autoHistory.map((h, i) => (
                <AutoRecalRow key={i} report={h} />
              ))}
            </div>
          </div>
        )}
      </div>

      {loading && <div className="flex justify-center py-10"><Loader2 size={20} className="animate-spin text-zinc-500" /></div>}

      {data?.error && (
        <div className="text-red-400 text-sm p-3 bg-red-500/10 rounded-lg border border-red-500/20">Errore: {data.error}</div>
      )}

      {current?.insufficient_data && (
        <div className="text-amber-400 text-sm p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
          Dati insufficienti ({current.n} predizioni) — servono almeno 30 match settati.
        </div>
      )}

      {current && !current.insufficient_data && !loading && (
        <>
          {/* Metric cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <MetricCard label="Predizioni" value={current.n} color="text-white" />
            <MetricCard label="Brier" value={current.brier?.toFixed(4)} hint={`baseline ${current.brier_baseline?.toFixed(3)}`} />
            <MetricCard label="ECE" value={`${(current.ece * 100).toFixed(2)}%`} color={getBadge('ece', current.ece)} hint="soglia alert 8%" />
            <MetricCard label="Log Loss" value={current.log_loss?.toFixed(4)} />
            <MetricCard
              label="Brier Skill"
              value={current.brier_skill_score >= 0 ? `+${current.brier_skill_score.toFixed(3)}` : current.brier_skill_score.toFixed(3)}
              color={getBadge('bss', current.brier_skill_score)}
              hint={current.brier_skill_score > 0 ? 'meglio del random' : 'peggio del random'}
            />
          </div>

          {/* Reliability diagram */}
          <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-sm font-medium text-zinc-200">Reliability Diagram</h3>
                <p className="text-xs text-zinc-500 mt-0.5">Probabilità prevista vs frequenza reale per bin (10 bucket)</p>
              </div>
              <div className="flex items-center gap-4 text-xs">
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-violet-500" /> Prevista</span>
                <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-emerald-500" /> Reale</span>
              </div>
            </div>
            <div className="space-y-1.5">
              {current.reliability?.map((b, i) => (
                <ReliabilityRow key={i} bin={b} />
              ))}
            </div>
          </div>

          {/* Interpretation helper */}
          <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 p-4 text-xs text-zinc-400 space-y-1.5">
            <p className="text-zinc-300 font-medium text-sm mb-2">Come leggere:</p>
            <p><span className="text-emerald-400">ECE &lt; 4%</span> = ben calibrato · <span className="text-amber-400">4–8%</span> = accettabile · <span className="text-red-400">&gt; 8%</span> = scalibrato</p>
            <p><span className="text-emerald-400">BSS &gt; 0</span> = modello utile · <span className="text-red-400">BSS &lt; 0</span> = peggio del predittore medio</p>
            <p>Nel diagramma: barre verdi ≈ barre viola → calibrato. Se verde &gt; viola, il modello sottostima. Se viola &gt; verde, sovrastima.</p>
          </div>
        </>
      )}
    </div>
  );
}

function MetricCard({ label, value, color = 'text-zinc-200', hint }) {
  return (
    <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 p-4">
      <div className="text-xs text-zinc-500 mb-1">{label}</div>
      <div className={`text-xl font-bold ${color}`}>{value ?? '—'}</div>
      {hint && <div className="text-[10px] text-zinc-600 mt-1">{hint}</div>}
    </div>
  );
}

function ReliabilityRow({ bin }) {
  const { bin_low, bin_high, count, expected, actual, gap } = bin;
  const empty = count === 0;
  const maxPct = 1.0;
  const expPct = empty ? 0 : (expected / maxPct) * 100;
  const actPct = empty ? 0 : (actual / maxPct) * 100;
  const gapColor = gap === null ? '' : Math.abs(gap) < 0.04 ? 'text-emerald-400' : Math.abs(gap) < 0.10 ? 'text-amber-400' : 'text-red-400';

  return (
    <div className="flex items-center gap-3 text-xs">
      <div className="w-20 text-zinc-500 font-mono tabular-nums">
        {(bin_low * 100).toFixed(0)}–{(bin_high * 100).toFixed(0)}%
      </div>
      <div className="w-12 text-right text-zinc-600 font-mono tabular-nums">{count}</div>
      <div className="flex-1 relative h-6">
        {!empty && (
          <>
            <div
              className="absolute top-0 h-2.5 bg-violet-500/70 rounded-sm"
              style={{ width: `${expPct}%` }}
              title={`Prevista: ${(expected * 100).toFixed(1)}%`}
            />
            <div
              className="absolute bottom-0 h-2.5 bg-emerald-500/70 rounded-sm"
              style={{ width: `${actPct}%` }}
              title={`Reale: ${(actual * 100).toFixed(1)}%`}
            />
          </>
        )}
      </div>
      <div className="w-24 text-right font-mono tabular-nums">
        {empty ? (
          <span className="text-zinc-700">—</span>
        ) : (
          <>
            <span className="text-violet-400">{(expected * 100).toFixed(1)}</span>
            <span className="text-zinc-600 mx-1">vs</span>
            <span className="text-emerald-400">{(actual * 100).toFixed(1)}</span>
          </>
        )}
      </div>
      <div className={`w-16 text-right font-mono tabular-nums ${gapColor}`}>
        {gap === null ? '' : `${gap >= 0 ? '+' : ''}${(gap * 100).toFixed(1)}`}
      </div>
    </div>
  );
}


function DriftTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refDays, setRefDays] = useState(14);
  const [refOffsetDays, setRefOffsetDays] = useState(7);
  const [curDays, setCurDays] = useState(7);
  const [recalibrating, setRecalibrating] = useState(false);
  const [recalMsg, setRecalMsg] = useState(null);

  const FEATURE_LABELS = {
    elo_like_diff: 'Elo-like Diff',
    form_diff_last5: 'Form Diff (last 5)',
    form_diff_last10: 'Form Diff (last 10)',
    market_prob_diff: 'Market Prob Diff',
    h2h_diff: 'H2H Diff',
    sets_diff_recent: 'Sets Diff Recent',
    sample_size_avg: 'Sample Size Avg',
  };

  const SEV_COLOR = {
    stable: 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30',
    slight: 'text-amber-400 bg-amber-500/10 border-amber-500/30',
    significant: 'text-red-400 bg-red-500/10 border-red-500/30',
    unknown: 'text-zinc-500 bg-zinc-800/40 border-zinc-700',
  };

  const loadLatest = async () => {
    try {
      const res = await apiGet('/tt/admin/drift-scan');
      if (res?.last_result) setData(res.last_result);
    } catch { /* ignore */ }
  };

  useEffect(() => { loadLatest(); }, []);

  const run = async () => {
    setLoading(true);
    try {
      await apiPost(`/tt/admin/drift-scan?ref_days=${refDays}&ref_offset_days=${refOffsetDays}&cur_days=${curDays}`);
      for (let i = 0; i < 30; i++) {
        await new Promise(r => setTimeout(r, 3000));
        const res = await apiGet('/tt/admin/drift-scan');
        if (!res?.running && res?.last_result) {
          setData(res.last_result);
          setLoading(false);
          return;
        }
      }
    } catch (e) {
      setData({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  const triggerRecalibrate = async () => {
    setRecalibrating(true);
    setRecalMsg(null);
    try {
      const r = await apiPost('/tt/admin/auto-recalibration?force=true');
      // Poll for completion
      for (let i = 0; i < 40; i++) {
        await new Promise(res => setTimeout(res, 3000));
        const st = await apiGet('/tt/admin/auto-recalibration');
        if (!st?.running && st?.last_result) {
          const res = st.last_result;
          const promoted = res.promoted?.length || 0;
          const rejected = res.rejected?.length || 0;
          const skipped = res.skipped?.length || 0;
          setRecalMsg(`Ricalibrazione: ${promoted} promossi · ${rejected} rigettati · ${skipped} saltati`);
          break;
        }
      }
    } catch (e) {
      setRecalMsg(`Errore: ${e.message || 'failed'}`);
    }
    setRecalibrating(false);
    setTimeout(() => setRecalMsg(null), 12000);
  };

  const features = data?.features || [];
  const tourneys = data?.composition?.tournaments || [];
  const players = data?.composition?.players;
  const alerts = data?.alerts || [];
  const windows = data?.windows;

  return (
    <div className="space-y-4" data-testid="drift-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-zinc-300 font-medium">Drift Detector — Cambio regime nelle feature</p>
          <p className="text-xs text-zinc-500 mt-0.5">PSI · KS · shift di composizione (tornei e giocatori)</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <label className="text-xs text-zinc-500 flex items-center gap-1">
            Ref
            <input
              type="number"
              value={refDays}
              onChange={e => setRefDays(Number(e.target.value) || 14)}
              data-testid="drift-ref-days"
              className="w-14 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-200"
            />gg
          </label>
          <label className="text-xs text-zinc-500 flex items-center gap-1">
            Offset
            <input
              type="number"
              value={refOffsetDays}
              onChange={e => setRefOffsetDays(Number(e.target.value) || 7)}
              data-testid="drift-offset-days"
              className="w-14 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-200"
            />gg
          </label>
          <label className="text-xs text-zinc-500 flex items-center gap-1">
            Cur
            <input
              type="number"
              value={curDays}
              onChange={e => setCurDays(Number(e.target.value) || 7)}
              data-testid="drift-cur-days"
              className="w-14 bg-zinc-800 border border-zinc-700 rounded px-2 py-1 text-xs text-zinc-200"
            />gg
          </label>
          <button
            onClick={run}
            disabled={loading}
            data-testid="drift-scan-btn"
            className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium disabled:opacity-50"
          >
            {loading ? <Loader2 size={14} className="animate-spin" /> : <BarChart3 size={14} />}
            {loading ? 'Scansione...' : 'Esegui Scan'}
          </button>
          <button
            onClick={triggerRecalibrate}
            disabled={recalibrating}
            data-testid="drift-recalibrate-btn"
            className="flex items-center gap-2 px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium disabled:opacity-50"
            title="Forza auto-recalibration champion/challenger ora (invece di attendere 05:45 CET)"
          >
            {recalibrating ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
            {recalibrating ? 'Ricalibro...' : 'Ricalibra ora'}
          </button>
        </div>
      </div>
      {recalMsg && (
        <div className="text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 rounded px-3 py-2" data-testid="drift-recal-msg">
          {recalMsg}
        </div>
      )}

      {!data && !loading && <p className="text-zinc-500 text-center py-10">Clicca "Esegui Scan" per rilevare drift su feature e composizione.</p>}

      {data?.error && <div className="text-red-400 text-sm p-3 bg-red-500/10 rounded-lg border border-red-500/20">Errore: {data.error}</div>}
      {data?.insufficient_data && (
        <div className="text-amber-400 text-sm p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
          Dati insufficienti: ref={windows?.reference?.n || 0} · cur={windows?.current?.n || 0}. Servono almeno 100 match per finestra.
        </div>
      )}

      {data && !data.insufficient_data && !data.error && (
        <>
          {/* Windows summary */}
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 p-4">
              <div className="text-xs text-zinc-500 mb-1">Finestra Reference</div>
              <div className="text-sm text-zinc-200 font-mono">{windows?.reference?.from} → {windows?.reference?.to}</div>
              <div className="text-lg font-bold text-violet-400 mt-1">{windows?.reference?.n || 0} match</div>
            </div>
            <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 p-4">
              <div className="text-xs text-zinc-500 mb-1">Finestra Current</div>
              <div className="text-sm text-zinc-200 font-mono">{windows?.current?.from} → {windows?.current?.to}</div>
              <div className="text-lg font-bold text-emerald-400 mt-1">{windows?.current?.n || 0} match</div>
            </div>
          </div>

          {/* Alerts */}
          {alerts.length > 0 && (
            <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-4">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={14} className="text-red-400" />
                <span className="text-sm font-semibold text-red-300">{alerts.length} alert attivi</span>
              </div>
              <ul className="space-y-1 text-sm text-red-200">
                {alerts.map((a, i) => <li key={i}>{a}</li>)}
              </ul>
            </div>
          )}

          {alerts.length === 0 && (
            <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-3 text-center">
              <CheckCircle size={18} className="inline text-emerald-400 mr-2" />
              <span className="text-emerald-300 text-sm font-medium">Nessun drift significativo rilevato</span>
            </div>
          )}

          {/* Feature drift table */}
          <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 overflow-hidden">
            <div className="px-4 py-3 border-b border-zinc-800">
              <h3 className="text-sm font-medium text-zinc-200">Feature drift</h3>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="text-zinc-500 border-b border-zinc-800">
                  <th className="text-left px-4 py-2 font-medium">Feature</th>
                  <th className="text-right px-3 py-2 font-medium">PSI</th>
                  <th className="text-right px-3 py-2 font-medium">KS</th>
                  <th className="text-right px-3 py-2 font-medium">Ref Mean</th>
                  <th className="text-right px-3 py-2 font-medium">Cur Mean</th>
                  <th className="text-right px-3 py-2 font-medium">Δ Mean</th>
                  <th className="text-center px-3 py-2 font-medium">Severità</th>
                </tr>
              </thead>
              <tbody>
                {features.map((f, i) => (
                  <tr key={i} className="border-b border-zinc-800/50 hover:bg-zinc-800/30">
                    <td className="px-4 py-2 text-zinc-200 font-medium">
                      {FEATURE_LABELS[f.feature] || f.feature}
                      {f.informational && (
                        <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] uppercase font-semibold bg-zinc-700/60 text-zinc-400 border border-zinc-600">INFO</span>
                      )}
                    </td>
                    <td className="text-right px-3 py-2 font-mono tabular-nums text-zinc-200">{f.psi ?? '—'}</td>
                    <td className="text-right px-3 py-2 font-mono tabular-nums text-zinc-400">{f.ks ?? '—'}</td>
                    <td className="text-right px-3 py-2 font-mono tabular-nums text-zinc-400">{f.reference?.mean ?? '—'}</td>
                    <td className="text-right px-3 py-2 font-mono tabular-nums text-zinc-400">{f.current?.mean ?? '—'}</td>
                    <td className={`text-right px-3 py-2 font-mono tabular-nums ${f.mean_shift > 0 ? 'text-emerald-400' : f.mean_shift < 0 ? 'text-red-400' : 'text-zinc-500'}`}>
                      {f.mean_shift > 0 ? '+' : ''}{f.mean_shift ?? '—'}
                    </td>
                    <td className="text-center px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-semibold border ${f.informational ? SEV_COLOR.unknown : SEV_COLOR[f.severity]}`}>
                        {f.informational ? 'informativo' : f.severity}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Composition: tournaments */}
          {tourneys.length > 0 && (
            <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 p-4">
              <h3 className="text-sm font-medium text-zinc-200 mb-3">Shift composizione tornei</h3>
              <div className="space-y-1">
                {tourneys.slice(0, 10).map((t, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs">
                    <div className="flex-1 text-zinc-300 truncate">{t.tournament}</div>
                    <div className="w-20 text-right text-zinc-500 font-mono tabular-nums">{(t.ref_share * 100).toFixed(1)}%</div>
                    <div className="w-8 text-center text-zinc-600">→</div>
                    <div className="w-20 text-right text-zinc-400 font-mono tabular-nums">{(t.cur_share * 100).toFixed(1)}%</div>
                    <div className={`w-16 text-right font-mono tabular-nums font-semibold ${t.shift > 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {t.shift > 0 ? '+' : ''}{(t.shift * 100).toFixed(1)}pp
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Players */}
          {players && (
            <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 p-4">
              <h3 className="text-sm font-medium text-zinc-200 mb-3">Pool giocatori</h3>
              <div className="grid grid-cols-4 gap-3 text-center">
                <div>
                  <div className="text-xl font-bold text-violet-400">{players.ref_total}</div>
                  <div className="text-xs text-zinc-500">Ref</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-emerald-400">{players.cur_total}</div>
                  <div className="text-xs text-zinc-500">Cur</div>
                </div>
                <div>
                  <div className="text-xl font-bold text-amber-400">{players.new_in_current}</div>
                  <div className="text-xs text-zinc-500">Nuovi</div>
                </div>
                <div>
                  <div className={`text-xl font-bold ${players.novelty_rate > 0.4 ? 'text-red-400' : players.novelty_rate > 0.25 ? 'text-amber-400' : 'text-emerald-400'}`}>
                    {(players.novelty_rate * 100).toFixed(0)}%
                  </div>
                  <div className="text-xs text-zinc-500">Novelty rate</div>
                </div>
              </div>
            </div>
          )}

          {/* Legend */}
          <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 p-4 text-xs text-zinc-400 space-y-1.5">
            <p className="text-zinc-300 font-medium text-sm mb-2">Come leggere:</p>
            <p><span className="text-emerald-400">PSI &lt; 0.10</span> = stabile · <span className="text-amber-400">0.10–0.25</span> = drift lieve · <span className="text-red-400">&gt; 0.25</span> = drift significativo</p>
            <p><b>KS</b>: max gap tra le CDF (0 = identiche, 1 = disgiunte). <b>Δ Mean</b>: differenza delle medie ref→cur.</p>
            <p>Drift alti su feature come <i>market_prob_diff</i> o <i>elo_like_diff</i> indicano possibile cambio di pool giocatori/tornei.</p>
          </div>
        </>
      )}
    </div>
  );
}


function AutoRecalRow({ report }) {
  const [expanded, setExpanded] = useState(false);
  const DEC_STYLE = {
    promoted: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
    rejected: 'bg-red-500/20 text-red-300 border-red-500/40',
    skipped: 'bg-zinc-700/30 text-zinc-400 border-zinc-700',
    completed: 'bg-violet-500/20 text-violet-300 border-violet-500/40',
  };
  const when = (report.created_at || report.run_at || '').slice(0, 16).replace('T', ' ');
  const triggered = report.trigger?.should_trigger;
  const promoted = report.promoted || [];
  const rejected = report.rejected || [];

  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-950/40 overflow-hidden">
      <div
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-zinc-900/60 text-xs"
      >
        <span className="text-zinc-500 font-mono tabular-nums w-32 flex-shrink-0">{when}</span>
        <span className={`px-2 py-0.5 rounded-sm border text-[10px] font-semibold uppercase ${DEC_STYLE[report.decision] || DEC_STYLE.completed}`}>
          {report.decision}
        </span>
        {!triggered && report.decision === 'skipped' && <span className="text-zinc-500">nessun trigger</span>}
        {promoted.length > 0 && <span className="text-emerald-400">✓ {promoted.join(', ')}</span>}
        {rejected.length > 0 && <span className="text-red-400">✗ {rejected.join(', ')}</span>}
        <span className="flex-1" />
        {report.trigger?.reasons?.length > 0 && (
          <span className="text-amber-400 text-[10px]">{report.trigger.reasons.length} trigger</span>
        )}
        <span className="text-zinc-600 text-[10px]">{expanded ? '▼' : '▶'}</span>
      </div>

      {expanded && (
        <div className="border-t border-zinc-800 p-3 space-y-2 bg-zinc-950/40 text-xs">
          {report.trigger?.reasons?.length > 0 && (
            <div className="text-zinc-400">
              <span className="text-zinc-500">Trigger: </span>
              {report.trigger.reasons.map((r, i) => <div key={i} className="text-amber-300 ml-2">• {r}</div>)}
            </div>
          )}
          {report.windows && (
            <div className="text-zinc-500 font-mono">
              Train: {report.windows.train?.from} → {report.windows.train?.to} · Holdout: {report.windows.holdout?.from} → {report.windows.holdout?.to}
            </div>
          )}
          {Object.entries(report.models || {}).map(([mk, mr]) => (
            <div key={mk} className="rounded border border-zinc-800 p-2 bg-zinc-900/40">
              <div className="flex items-center justify-between mb-1">
                <span className="text-zinc-200 font-medium">{mk}</span>
                <span className={`px-2 py-0.5 rounded text-[10px] uppercase font-semibold border ${DEC_STYLE[mr.decision] || 'bg-zinc-700 text-zinc-400'}`}>
                  {mr.decision}
                </span>
              </div>
              {mr.reason && <div className="text-zinc-500 text-[10px] mb-1">{mr.reason}</div>}
              <div className="text-zinc-500 text-[10px]">train={mr.train_n} · holdout={mr.holdout_n}</div>
              {mr.champion_metrics?.ece !== null && mr.champion_metrics?.ece !== undefined && (
                <table className="w-full mt-2 text-[10px] font-mono tabular-nums">
                  <thead className="text-zinc-500">
                    <tr>
                      <th className="text-left">Modello</th>
                      <th className="text-right">ECE</th>
                      <th className="text-right">Brier</th>
                      <th className="text-right">LogLoss</th>
                      <th className="text-right">BSS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mr.raw_metrics && (
                      <tr className="text-zinc-400">
                        <td>raw (no cal)</td>
                        <td className="text-right">{mr.raw_metrics.ece}</td>
                        <td className="text-right">{mr.raw_metrics.brier}</td>
                        <td className="text-right">{mr.raw_metrics.log_loss}</td>
                        <td className="text-right">—</td>
                      </tr>
                    )}
                    <tr className="text-violet-300">
                      <td>champion</td>
                      <td className="text-right">{mr.champion_metrics.ece}</td>
                      <td className="text-right">{mr.champion_metrics.brier}</td>
                      <td className="text-right">{mr.champion_metrics.log_loss}</td>
                      <td className="text-right">{mr.champion_metrics.brier_skill_score}</td>
                    </tr>
                    <tr className={mr.decision === 'promoted' ? 'text-emerald-300' : 'text-zinc-300'}>
                      <td>challenger</td>
                      <td className="text-right">{mr.challenger_metrics?.ece}</td>
                      <td className="text-right">{mr.challenger_metrics?.brier}</td>
                      <td className="text-right">{mr.challenger_metrics?.log_loss}</td>
                      <td className="text-right">{mr.challenger_metrics?.brier_skill_score}</td>
                    </tr>
                  </tbody>
                </table>
              )}
              <div className="text-zinc-500 text-[10px] mt-1">
                Δ ECE: <span className={mr.ece_improvement > 0 ? 'text-emerald-400' : 'text-red-400'}>{mr.ece_improvement > 0 ? '+' : ''}{mr.ece_improvement}</span>
                {' · '}
                Δ Brier: <span className={mr.brier_regression < 0 ? 'text-emerald-400' : 'text-red-400'}>{mr.brier_regression > 0 ? '+' : ''}{mr.brier_regression}</span>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function HealthTab() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const h = await apiGet('/tt/admin/system-health');
      setHealth(h);
    } catch (e) {
      setHealth({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const iv = setInterval(load, 60000); // refresh every minute
    return () => clearInterval(iv);
  }, []);

  const STATUS_STYLE = {
    green: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', dot: 'bg-emerald-400' },
    yellow: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', dot: 'bg-amber-400' },
    red: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', dot: 'bg-red-400' },
  };
  const STATUS_LABEL = { green: 'OPERATIVO', yellow: 'DEGRADATO', red: 'CRITICO' };

  if (loading && !health) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-zinc-500" size={24} /></div>;
  if (health?.error) return <div className="text-red-400 p-4 bg-red-500/10 rounded-lg border border-red-500/20">Errore: {health.error}</div>;
  if (!health) return null;

  const overall = health.overall_status || 'yellow';
  const oStyle = STATUS_STYLE[overall];

  return (
    <div className="space-y-5" data-testid="health-tab">
      {/* Overall status banner */}
      <div className={`rounded-xl border p-6 ${oStyle.bg} ${oStyle.border}`}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className={`w-4 h-4 rounded-full ${oStyle.dot}`} />
              <div className={`absolute inset-0 w-4 h-4 rounded-full ${oStyle.dot} animate-ping opacity-60`} />
            </div>
            <div>
              <div className="text-xs text-zinc-500 uppercase tracking-wide font-semibold">System Health</div>
              <div className={`text-2xl font-bold ${oStyle.text}`}>{STATUS_LABEL[overall]}</div>
            </div>
          </div>
          <div className="text-xs text-zinc-500">
            Aggiornato: {(health.computed_at || '').slice(11, 16)} UTC · refresh ogni 60s
          </div>
        </div>
      </div>

      {/* Check grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {(health.checks || []).map((c, i) => {
          const st = STATUS_STYLE[c.status];
          return (
            <div key={i} data-testid={`health-check-${c.name.toLowerCase().replace(/ /g, '-')}`} className={`rounded-xl border p-4 ${st.bg} ${st.border}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className={`w-2 h-2 rounded-full ${st.dot}`} />
                <span className="text-sm font-semibold text-zinc-200">{c.name}</span>
                <span className={`ml-auto text-[10px] uppercase font-bold ${st.text}`}>{c.status}</span>
              </div>
              <div className="text-lg font-bold text-white mt-1">{c.metric}</div>
              <div className="text-[11px] text-zinc-500 mt-2 leading-relaxed">{c.detail}</div>
            </div>
          );
        })}
      </div>

      {/* Calibration models breakdown */}
      {health.calibration_models?.length > 0 && (
        <div className="bg-zinc-900/80 rounded-xl border border-zinc-800 overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between">
            <h3 className="text-sm font-medium text-zinc-200">Modelli calibrati</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full ${health.calibration_enabled ? 'bg-emerald-500/20 text-emerald-300' : 'bg-zinc-700 text-zinc-400'}`}>
              {health.calibration_enabled ? 'Calibrazione ON' : 'Calibrazione OFF'}
            </span>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-zinc-500 border-b border-zinc-800/70">
                <th className="text-left px-4 py-2 font-medium">Modello</th>
                <th className="text-right px-3 py-2 font-medium">ECE</th>
                <th className="text-right px-3 py-2 font-medium">Brier</th>
                <th className="text-right px-3 py-2 font-medium">BSS</th>
                <th className="text-right px-3 py-2 font-medium">N</th>
                <th className="text-center px-3 py-2 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {health.calibration_models.map((m, i) => {
                const st = STATUS_STYLE[m.status];
                return (
                  <tr key={i} className="border-b border-zinc-800/40">
                    <td className="px-4 py-2 text-zinc-200 font-medium">{m.model}</td>
                    <td className={`text-right px-3 py-2 font-mono tabular-nums ${st.text}`}>{(m.ece * 100).toFixed(2)}%</td>
                    <td className="text-right px-3 py-2 font-mono tabular-nums text-zinc-400">{m.brier}</td>
                    <td className="text-right px-3 py-2 font-mono tabular-nums text-zinc-400">
                      {m.bss >= 0 ? '+' : ''}{m.bss}
                    </td>
                    <td className="text-right px-3 py-2 font-mono tabular-nums text-zinc-500">{m.n}</td>
                    <td className="text-center px-3 py-2">
                      <div className={`inline-block w-2 h-2 rounded-full ${st.dot}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="text-[11px] text-zinc-600 px-1">
        <p>Nota: ECE misurato sulle predizioni deployed. Dopo l'attivazione della calibrazione, serve ~2-3 settimane di nuove predizioni affinché ECE scenda ai valori target (&lt;4%).</p>
      </div>
    </div>
  );
}



// ============================================================
// Filter Diff Tab — Manual calibration mode
// ============================================================
function FilterDiffTab() {
  const [diff, setDiff] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [msg, setMsg] = useState(null);
  const [proposals, setProposals] = useState([]);

  const loadProposals = async () => {
    try {
      const r = await apiGet('/tt/admin/filter-diff/proposals');
      setProposals(r?.proposals || []);
    } catch (e) {
      // non-fatal
    }
  };

  useEffect(() => { loadProposals(); }, []);

  const simulate = async () => {
    setLoading(true);
    setMsg(null);
    try {
      const r = await apiPost('/tt/admin/filter-diff/run?dry_run=true', {});
      setDiff(r);
      setMsg(r?.any_trigger ? 'Simulazione completata: gap rilevato' : 'Simulazione: nessun gap, filtri allineati');
    } catch (e) {
      setMsg(`Errore: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  const runAndSend = async () => {
    setApplying(true);
    setMsg(null);
    try {
      const r = await apiPost('/tt/admin/filter-diff/run', {});
      setDiff(r);
      await loadProposals();
      if (r?.token) {
        setMsg(`Proposta creata: ${r.token}${r.telegram_sent ? ' · Telegram inviato' : ''}`);
      } else {
        setMsg('Nessuna proposta creata (nessun gap)');
      }
    } catch (e) {
      setMsg(`Errore: ${e.message}`);
    } finally {
      setApplying(false);
    }
  };

  const applyProposal = async (token) => {
    setApplying(true);
    try {
      const r = await apiPost('/tt/admin/filter-diff/apply', { token });
      setMsg(r.status === 'applied'
        ? `Applicato: ${(r.applied_markets || []).join(', ') || 'nessun mercato'}`
        : `Stato: ${r.status}`);
      await loadProposals();
    } catch (e) {
      setMsg(`Errore: ${e.message}`);
    } finally {
      setApplying(false);
    }
  };

  const rejectProposal = async (token) => {
    setApplying(true);
    try {
      const r = await apiPost('/tt/admin/filter-diff/reject', { token });
      setMsg(`Proposta ${token}: ${r.status}`);
      await loadProposals();
    } catch (e) {
      setMsg(`Errore: ${e.message}`);
    } finally {
      setApplying(false);
    }
  };

  const markets = diff?.diff?.markets || {};
  const marketLabels = {
    match_winner: { title: 'Match Winner', short: 'MW', accent: 'text-blue-400', border: 'border-blue-500/30' },
    handicap: { title: 'Handicap', short: 'HC', accent: 'text-amber-400', border: 'border-amber-500/30' },
    over_under: { title: 'Over / Under', short: 'O/U', accent: 'text-purple-400', border: 'border-purple-500/30' },
  };

  return (
    <div className="space-y-5" data-testid="filter-diff-tab">
      <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-4">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div>
            <div className="text-sm font-semibold text-zinc-200">Filter Diff — Manual calibration</div>
            <div className="text-xs text-zinc-500 mt-0.5">
              Confronta filtri live vs miglior combo dell'optimizer (30gg). Simula senza applicare o invia proposta Telegram.
            </div>
          </div>
          <div className="flex gap-2 flex-wrap">
            <button
              data-testid="filter-diff-simulate-btn"
              onClick={simulate}
              disabled={loading || applying}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-sky-600/90 hover:bg-sky-500 text-white text-sm font-medium transition disabled:opacity-50"
            >
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Search size={14} />}
              Simula
            </button>
            <button
              data-testid="filter-diff-send-btn"
              onClick={runAndSend}
              disabled={loading || applying}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-white text-sm font-medium transition disabled:opacity-50"
              title="Genera una proposta persistente e invia notifica Telegram"
            >
              {applying ? <Loader2 size={14} className="animate-spin" /> : <Bot size={14} />}
              Genera + Telegram
            </button>
          </div>
        </div>
        {msg && (
          <div className="mt-3 text-xs text-emerald-400" data-testid="filter-diff-msg">{msg}</div>
        )}
      </div>

      {/* Pending proposals */}
      {proposals.length > 0 && (
        <div className="rounded-xl border border-violet-500/30 bg-violet-500/5 p-4" data-testid="filter-diff-proposals">
          <div className="flex items-center gap-2 mb-3 text-violet-300 text-xs uppercase tracking-wider font-semibold">
            <Bot size={14} />
            Proposte in sospeso ({proposals.length})
          </div>
          <div className="space-y-2">
            {proposals.map(p => (
              <div
                key={p.token}
                data-testid={`proposal-${p.token}`}
                className="flex items-center justify-between gap-3 p-3 rounded-lg bg-zinc-900/60 border border-zinc-800"
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-mono text-zinc-200">{p.token}</div>
                  <div className="text-[11px] text-zinc-500 mt-0.5">
                    Creata: {p.created_at} · Scade: {p.expires_at}
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-1">
                    Mercati trigger: {Object.entries(p.diff?.markets || {})
                      .filter(([, e]) => e.triggers_alert)
                      .map(([m, e]) => `${m}(Δ${e.delta_roi_pp > 0 ? '+' : ''}${e.delta_roi_pp}pp)`)
                      .join(', ') || 'nessuno'}
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <button
                    data-testid={`apply-proposal-${p.token}`}
                    onClick={() => applyProposal(p.token)}
                    disabled={applying}
                    className="px-3 py-1.5 rounded-md bg-emerald-600 hover:bg-emerald-500 text-white text-xs font-medium disabled:opacity-50"
                  >
                    Applica
                  </button>
                  <button
                    data-testid={`reject-proposal-${p.token}`}
                    onClick={() => rejectProposal(p.token)}
                    disabled={applying}
                    className="px-3 py-1.5 rounded-md bg-zinc-700 hover:bg-zinc-600 text-zinc-200 text-xs font-medium disabled:opacity-50"
                  >
                    Scarta
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Markets side-by-side */}
      {diff && (
        <div className="space-y-3" data-testid="filter-diff-results">
          <div className="text-xs text-zinc-500">
            Generato: {diff?.diff?.generated_at} ·
            Finestra: {diff?.diff?.config?.days}gg ·
            Soglia alert: Δ ROI ≥ {diff?.diff?.config?.min_roi_delta_pp}pp
            {diff?.dry_run && ' · (DRY RUN)'}
          </div>
          {Object.entries(markets).map(([mkt, entry]) => {
            const cfg = marketLabels[mkt] || { title: mkt, short: '?', accent: 'text-zinc-300', border: 'border-zinc-700' };
            const stats = entry.live_stats;
            const best = entry.best_combo;
            return (
              <div
                key={mkt}
                data-testid={`filter-diff-market-${mkt}`}
                className={`rounded-xl border ${entry.triggers_alert ? cfg.border : 'border-zinc-800'} bg-zinc-900/40 p-4`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className={`px-2 py-0.5 text-[10px] font-bold rounded bg-zinc-800 ${cfg.accent} border ${cfg.border}`}>
                      {cfg.short}
                    </span>
                    <span className="text-sm font-medium text-zinc-200">{cfg.title}</span>
                    {entry.triggers_alert && (
                      <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-violet-500/20 text-violet-300 border border-violet-500/30">
                        TRIGGER
                      </span>
                    )}
                  </div>
                  {entry.delta_roi_pp !== null && entry.delta_roi_pp !== undefined && (
                    <span className={`text-sm font-mono tabular-nums ${entry.triggers_alert ? 'text-violet-300' : 'text-zinc-400'}`}>
                      Δ {entry.delta_roi_pp > 0 ? '+' : ''}{entry.delta_roi_pp}pp
                    </span>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* LIVE */}
                  <div className="rounded-lg border border-zinc-800 bg-zinc-900/60 p-3">
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 font-semibold">Live (attuale)</div>
                    {stats ? (
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-zinc-400">Picks 30gg</span>
                          <span className="font-mono text-zinc-200">{stats.n}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-400">W-L-V</span>
                          <span className="font-mono text-zinc-200">{stats.wins}-{stats.losses}-{stats.voids}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-400">WR</span>
                          <span className="font-mono text-zinc-200">{stats.wr_pct}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-400">Stake / PnL</span>
                          <span className="font-mono text-zinc-200">{stats.stake}u / <span className={stats.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}>{stats.pnl >= 0 ? '+' : ''}{stats.pnl}u</span></span>
                        </div>
                        <div className="flex justify-between border-t border-zinc-800 pt-1 mt-1">
                          <span className="text-zinc-400 font-semibold">ROI</span>
                          <span className={`font-mono font-bold ${stats.roi_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {stats.roi_pct >= 0 ? '+' : ''}{stats.roi_pct}%
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-zinc-500 italic">Nessun dato live (mercato non ha picks negli ultimi 30gg)</div>
                    )}
                    <div className="mt-2 pt-2 border-t border-zinc-800">
                      <div className="text-[10px] uppercase text-zinc-500 mb-1">Filtri attivi</div>
                      <pre className="text-[10px] font-mono text-zinc-400 whitespace-pre-wrap break-all">{JSON.stringify(entry.live_filters, null, 2)}</pre>
                    </div>
                  </div>

                  {/* BEST COMBO */}
                  <div className={`rounded-lg border ${entry.triggers_alert ? cfg.border : 'border-zinc-800'} bg-zinc-900/60 p-3`}>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2 font-semibold">Best combo optimizer</div>
                    {best ? (
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-zinc-400">Sample</span>
                          <span className="font-mono text-zinc-200">n={best.n}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-400">WR</span>
                          <span className="font-mono text-zinc-200">{best.WR}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-zinc-400">PnL</span>
                          <span className={`font-mono ${best.PnL >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {best.PnL >= 0 ? '+' : ''}{best.PnL}
                          </span>
                        </div>
                        <div className="flex justify-between border-t border-zinc-800 pt-1 mt-1">
                          <span className="text-zinc-400 font-semibold">ROI</span>
                          <span className={`font-mono font-bold ${best.ROI >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {best.ROI >= 0 ? '+' : ''}{best.ROI}%
                          </span>
                        </div>
                      </div>
                    ) : (
                      <div className="text-xs text-zinc-500 italic">Nessuna combo trovata con sample ≥ soglia</div>
                    )}
                    <div className="mt-2 pt-2 border-t border-zinc-800">
                      <div className="text-[10px] uppercase text-zinc-500 mb-1">Filtri proposti</div>
                      <pre className="text-[10px] font-mono text-zinc-400 whitespace-pre-wrap break-all">{JSON.stringify(entry.proposed_filters, null, 2)}</pre>
                    </div>
                    {best?.signature && (
                      <div className="mt-2 text-[10px] text-zinc-500 font-mono break-all">{best.signature}</div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!diff && !loading && (
        <div className="rounded-xl border border-dashed border-zinc-800 bg-zinc-900/30 p-6 text-center">
          <div className="text-sm text-zinc-400">Premi <b className="text-zinc-200">Simula</b> per confrontare filtri live vs best combo.</div>
          <div className="text-xs text-zinc-500 mt-1">Le proposte pending sono applicabili anche via Telegram con <code className="bg-zinc-800 px-1 rounded">/apply_diff &lt;token&gt;</code>.</div>
        </div>
      )}
    </div>
  );
}

